import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import io from 'socket.io-client';
import useServiceContextHook from '../_hooks/service.context.hook';
import { getStoredAuthToken } from '../_helpers/authTokenStorage.helper';
import {
  buildInitialSelectedSport,
  readSportsPageState,
  writeSportsPageState,
} from '../pages/Sports/_helpers/sportsPageState';
import { buildPrematchRecoveryRefreshParams } from '../pages/Sports/_helpers/prematchRecoveryRefresh';
import {
  buildMarketSuspendOddDisplayGuardPayload,
  buildInplayChangedKeySet,
  buildInplaySocketSubscriptionDiff,
  buildInplaySocketSubscriptionKey,
  commitMarketSuspendOddDisplayGuard,
  commitRealtimeOrderingGuard,
  compareRealtimeOrderingInfo,
  createRealtimeOrderingGuard,
  mergeInplayMatchWithDisplayGuard,
  normalizeRealtimeOrderingInfo,
  shouldApplyRealtimeOrderingGuard,
} from '../pages/Sports/_helpers/inplayDisplayOrderingGuard';
import { isTerminalLiveMatchStatus } from '../pages/Sports/_helpers/inplayStickySubscription';
import {
  buildLiveScoreShellPatch,
  hasLiveScoreShell,
  isHiddenLiveScoreShell,
  isTerminalLiveScoreShell,
  mergeLiveScoreShellFields,
  shouldPreserveLiveScoreShell,
} from '../pages/Sports/_helpers/inplayLiveScoreShell';
import {
  applyInplayMarketsPatchToMarketTree,
  buildInplayMarketsPatchSelection,
  collectInplayMarketTreeRefs,
  getInplayMarketsPatchOddId,
} from '../pages/Sports/_helpers/inplayMarketsPatchState';
import {
  getCustomerMarketKey,
  getInplaySelectionStableKey,
} from '../_helpers/inplayMarketIdentity';

/** market의 odds collection getter — odds 또는 selections 키 호환 */
const getMarketOdds = (mk) => mk?.odds || mk?.selections || [];
const getLineGroupOdds = (lineGroup) => lineGroup?.odds || lineGroup?.selections || [];
const eachMarketOdd = (market, cb) => {
  for (const o of getMarketOdds(market)) cb(o, market);
  if (Array.isArray(market?.lineGroups)) {
    for (const lineGroup of market.lineGroups) {
      for (const o of getLineGroupOdds(lineGroup)) cb(o, market, lineGroup);
    }
  }
};
/** market의 odds를 안전하게 교체 (원본 키 유지) */
const setMarketOdds = (mk, mapper) => {
  const key = mk.odds ? 'odds' : mk.selections ? 'selections' : 'odds';
  return { ...mk, [key]: (mk[key] || []).map(mapper) };
};

/**
 * match에 표시되는 모든 odds를 순회하는 helper.
 * markets 안의 odds/selections + handicap_pair의 home/away selection을 모두 포함.
 * 중복 id는 한 번만 방문.
 */
const eachDisplayedOdd = (match, cb) => {
  const seen = new Set();
  const visit = (o, market, lineGroup) => {
    if (o?.id && !seen.has(o.id)) {
      seen.add(o.id);
      cb(o, market, lineGroup);
    }
  };
  for (const mk of (match.markets || [])) {
    eachMarketOdd(mk, visit);
  }
  const hp = match.handicap_pair;
  if (hp) {
    if (hp.home_selection) visit(hp.home_selection);
    if (hp.away_selection) visit(hp.away_selection);
  }
};

const getSelectionFlashIdentityKey = (selection = {}) => (
  selection.flashIdentityKey
  || selection.flash_identity_key
  || selection.v2_display_model?.flashIdentityKey
  || selection.v2_identity?.flashIdentityKey
  || selection.v2_identity?.identityKey
  || null
);

const getSelectionPriceIdentityKey = (selection = {}) => {
  const stableKey = getInplaySelectionStableKey(selection);
  if (stableKey) return stableKey;
  const direct = getSelectionFlashIdentityKey(selection);
  if (direct) return String(direct);
  const v2 = selection.v2_identity || {};
  const parts = [
    v2.fixtureId,
    v2.marketId,
    v2.productMarketId || '__compat__',
    v2.lineId,
    v2.selectionId,
  ];
  if (parts.every((part) => part !== null && part !== undefined && String(part).trim())) {
    return `v2:${parts.map((part) => String(part).trim()).join('|')}`;
  }
  const id = selection.id ?? selection.odds_id ?? selection.oddsId;
  return id == null ? null : `id:${id}`;
};

const getSelectionLabelSignature = (selection = {}) => String(
  selection.selectionDisplayName
  || selection.selection_display_label
  || selection.name_ko
  || selection.name
  || selection.displayLabel
  || selection.selectionLabel
  || ''
);

const getSelectionStateSignature = (selection = {}) => JSON.stringify({
  odds: toFiniteNumber(selection?.odds),
  is_suspended: selection?.is_suspended ?? selection?.suspended ?? null,
  visible: selection?.v2_read_model?.visible ?? selection?.visible ?? null,
  bettable: selection?.v2_read_model?.bettable ?? null,
  reason: selection?.v2_read_model?.reason ?? null,
  currentSellable: selection?.v2_contract_fields?.currentSellable ?? null,
});

const collectDisplayedSelectionsByIdentity = (matchOrMarkets) => {
  const markets = Array.isArray(matchOrMarkets)
    ? matchOrMarkets
    : (Array.isArray(matchOrMarkets?.markets) ? matchOrMarkets.markets : []);
  const selections = new Map();
  markets.forEach((market) => {
    eachMarketOdd(market, (selection) => {
      const key = getSelectionPriceIdentityKey(selection);
      if (key) selections.set(key, selection);
    });
  });
  return selections;
};

const countExplicitDisplayHiddenSelections = (markets = []) => {
  let count = 0;
  markets.forEach((market) => {
    eachMarketOdd(market, (selection) => {
      const readModel = selection?.v2_read_model || {};
      const closure = selection?.closureBucket
        || selection?.closure_bucket
        || selection?.closureReason
        || selection?.closure_reason
        || readModel.closureBucket
        || readModel.closureReason;
      if ((readModel.visible === false || selection?.visible === false || selection?.is_visible === false) && closure) {
        count += 1;
      }
    });
  });
  return count;
};

const markInplayV2BrowserDebug = (patch = {}) => {
  if (typeof window === 'undefined') return;
  window.__sportsInplayV2Debug = {
    ...(window.__sportsInplayV2Debug || {}),
    ...patch,
  };
};

const FULL_REPLACE_AUTHORITY_REASONS = new Set([
  'terminal_authority',
  'lifecycle_authority',
  'status_authority',
  'structural_market_change',
  'main_line_replacement',
  'strict_pair_shape_change',
  'read_model_full_bootstrap',
  'recovery_replace',
  'unknown_full_replace',
]);

const getMarketsReplaceReason = (payload = {}) => String(
  payload?.replaceReason
  || payload?.replace_reason
  || payload?.observe?.replaceReason
  || payload?.observe?.replace_reason
  || ''
).trim().toLowerCase();

const getMarketsReplaceProjectionMode = (payload = {}) => String(
  payload?.projectionMode
  || payload?.projection_mode
  || ''
).trim().toLowerCase();

const buildChangedKeyPayload = (payload = {}) => ({
  changedIdentityKeys: Array.isArray(payload?.changedIdentityKeys)
    ? payload.changedIdentityKeys
    : (Array.isArray(payload?.changed_identity_keys) ? payload.changed_identity_keys : []),
  changedLineSetKeys: Array.isArray(payload?.changedLineSetKeys)
    ? payload.changedLineSetKeys
    : (Array.isArray(payload?.changed_line_set_keys) ? payload.changed_line_set_keys : []),
  changedMarketKeys: Array.isArray(payload?.changedMarketKeys)
    ? payload.changedMarketKeys
    : (Array.isArray(payload?.changed_market_keys) ? payload.changed_market_keys : []),
});

const hasChangedKeyPayload = (payload = {}) => {
  const changedKeys = buildChangedKeyPayload(payload);
  return changedKeys.changedIdentityKeys.length > 0
    || changedKeys.changedLineSetKeys.length > 0
    || changedKeys.changedMarketKeys.length > 0;
};

const isDiffFriendlyMarketsReplace = (payload = {}, isFullFixtureProjection = false) => {
  if (!isFullFixtureProjection || !hasChangedKeyPayload(payload)) return false;
  const reason = getMarketsReplaceReason(payload);
  if (FULL_REPLACE_AUTHORITY_REASONS.has(reason)) return false;
  const mode = getMarketsReplaceProjectionMode(payload);
  return reason === 'price_only_folded'
    || reason === 'periodic_projection'
    || mode === 'price_diff'
    || mode === 'v2_common_display_model';
};

const hasChangedIdentityKey = (changedIdentityKeySet = new Set(), key = null) => {
  if (!key) return false;
  const normalized = String(key).trim();
  if (!normalized) return false;
  return changedIdentityKeySet.has(normalized)
    || (normalized.startsWith('v2:') && changedIdentityKeySet.has(normalized.slice(3)))
    || changedIdentityKeySet.has(`v2:${normalized}`);
};

const countPreservedFullProjectionSelections = ({
  previousSelectionsByIdentity = new Map(),
  incomingSelectionsByIdentity = new Map(),
  changedIdentityKeySet = new Set(),
} = {}) => {
  let count = 0;
  incomingSelectionsByIdentity.forEach((_selection, key) => {
    if (previousSelectionsByIdentity.has(key) && !hasChangedIdentityKey(changedIdentityKeySet, key)) count += 1;
  });
  return count;
};

/** 스포츠 데이터 상태 관리 — Socket.IO 실시간 + 초기 REST 로드 */
export const SportsContext = createContext();

/** API 응답에서 안전하게 배열 추출 */
const toArray = (data, key) => {
  if (!data) return [];
  if (key && data[key] && Array.isArray(data[key])) return data[key];
  if (data.rows && Array.isArray(data.rows)) return data.rows;
  if (Array.isArray(data)) return data;
  return [];
};

const normalizeSocketBettingCategory = (value) => {
  const normalized = String(value || '').trim().toUpperCase();
  return normalized || null;
};

const getSocketPayloadMatchId = (payload = {}) => {
  const directMatchId = payload?.match_id ?? payload?.matchId;
  if (directMatchId != null) return directMatchId;

  const looksLikeMarketOrOddsPayload = (
    payload?.odds_id != null
    || payload?.market_id != null
    || payload?.odds !== undefined
    || payload?.odds_ids !== undefined
  );
  return looksLikeMarketOrOddsPayload ? null : payload?.id;
};

const shouldAcceptSocketPayload = (bettingType, payload = {}, isKnownLiveMatch = () => false) => {
  if (bettingType !== 'LIVE') return true;
  const category = normalizeSocketBettingCategory(payload?.betting_category);
  if (category === 'LIVE') return true;
  if (category) return false;
  return isKnownLiveMatch(payload);
};

const isSameSocketId = (left, right) => {
  if (left == null || right == null) return false;

  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber === rightNumber;
  }

  return String(left) === String(right);
};

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const getLatestOddsValue = (latestOddsMap, oddsId, fallback) => {
  const latestValue = toFiniteNumber(latestOddsMap?.[oddsId]?.odds);
  if (latestValue !== null) return latestValue;
  return toFiniteNumber(fallback);
};

const isLockedFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

const shouldPreserveRecentRealtimeOdds = (prevEntry, incomingSelection, options = {}) => {
  const prevOrdering = normalizeRealtimeOrderingInfo(prevEntry || {});
  const incomingOrdering = normalizeRealtimeOrderingInfo(incomingSelection || {});
  if (prevOrdering.hasOrderingSignal) {
    return compareRealtimeOrderingInfo(incomingOrdering, prevOrdering) < 0;
  }
  const incomingSource = String(options.source || incomingSelection?.source || '');
  if (
    String(prevEntry?.source || '').startsWith('ws')
    && !incomingSource.startsWith('ws')
    && !incomingOrdering.hasOrderingSignal
  ) {
    return true;
  }
  return false;
};

const buildOddsLatestEntry = (prevEntry, selection, options = {}) => {
  const now = Number(options.now || Date.now());
  if (
    options.forceOverwrite !== true
    && shouldPreserveRecentRealtimeOdds(prevEntry, selection, { ...options, now })
  ) {
    return prevEntry;
  }

  const nextOdds = toFiniteNumber(selection?.odds);
  const orderingInfo = normalizeRealtimeOrderingInfo(selection || {});
  return {
    ...(prevEntry || {}),
    ...(nextOdds !== null && { odds: nextOdds }),
    ...(selection?.is_suspended !== undefined && { is_suspended: selection.is_suspended }),
    ...(orderingInfo.authority_ts ? { authority_ts: orderingInfo.authority_ts } : {}),
    ...(orderingInfo.sequence !== null ? { sequence: orderingInfo.sequence } : {}),
    updatedAt: now,
    source: options.source || 'rest',
  };
};

const ALWAYS_REST_POLL_BETTING_TYPES = new Set(['DOMESTIC', 'INTERNATIONAL', 'SPECIAL']);
const LIVE_COUNT_REFRESH_INTERVAL_MS = 15000;
const LIVE_MATCH_LIST_CACHE_TTL_MS = 12000;
const LIVE_CARD_LIST_CACHE_TTL_MS = 25000;
const LIVE_CARD_LIST_LIMIT = 200;
const LIVE_LIST_INVALIDATE_DEBOUNCE_MS = 800;

const isDocumentHidden = () => typeof document !== 'undefined' && document.hidden;
const withLiveNoCacheParams = (params = {}, bettingType = '') => (
  bettingType === 'LIVE'
    ? { ...params, _live_ts: Date.now() }
    : params
);

const buildSocketMatchSubscriptions = (matchIds = [], bettingType = 'DOMESTIC') => {
  const uniqueIds = [...new Set(
    matchIds
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value))
  )];

  if (bettingType === 'LIVE') {
    return uniqueIds.map((id) => ({ id, betting_category: 'LIVE' }));
  }

  return uniqueIds;
};

const buildSportsnewFixtureSubscriptions = (matchIds = [], bettingType = 'DOMESTIC') => {
  if (bettingType !== 'LIVE') return [];
  return [...new Set(
    matchIds
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value))
  )].map((fixtureId) => ({ fixtureId, mode: 'live' }));
};

const sportsnewFixtureSubscriptionKey = (subscription = {}) =>
  `sportsnew:fixture:${subscription.mode || 'live'}:${subscription.fixtureId}`;

const initialSportsPageState = readSportsPageState();
const getSportsRouteBettingType = (search = '') => {
  if (typeof window === 'undefined') return null;

  try {
    const params = new URLSearchParams(search);
    const normalizedType = String(params.get('type') || '').trim().toUpperCase();
    return ['DOMESTIC', 'SPECIAL', 'INTERNATIONAL', 'LIVE'].includes(normalizedType)
      ? normalizedType
      : null;
  } catch (_) {
    return null;
  }
};

const getSportsRoutePage = (search = '') => {
  if (typeof window === 'undefined') return null;

  try {
    const params = new URLSearchParams(search);
    const page = Number(params.get('page') || params.get('live_page'));
    return Number.isFinite(page) && page > 0 ? Math.floor(page) : null;
  } catch (_) {
    return null;
  }
};

const getSportsRouteSportId = (search = '') => {
  if (typeof window === 'undefined') return null;

  try {
    const params = new URLSearchParams(search);
    const sportId = Number(params.get('sport_id') || params.get('sportId'));
    return Number.isFinite(sportId) && sportId > 0 ? Math.floor(sportId) : null;
  } catch (_) {
    return null;
  }
};

const initialRouteBettingType = (
  typeof window !== 'undefined' && window.location?.pathname === '/sports'
    ? getSportsRouteBettingType(window.location.search)
    : null
);
const initialRouteSportId = (
  typeof window !== 'undefined' && window.location?.pathname === '/sports'
    ? getSportsRouteSportId(window.location.search)
    : null
);
const initialBettingType = initialRouteBettingType || initialSportsPageState?.bettingType || 'DOMESTIC';
const initialSelectedSportId = initialBettingType === 'LIVE'
  ? initialRouteSportId
  : initialRouteBettingType === 'DOMESTIC'
    ? null
    : initialSportsPageState?.selectedSportId;
const initialRouteMatchPage = (
  typeof window !== 'undefined'
  && window.location?.pathname === '/sports'
  && initialBettingType === 'LIVE'
    ? getSportsRoutePage(window.location.search)
    : null
);

const buildLiveMatchPageCacheKey = (listScopeKey, page) => `${listScopeKey}|page:${page}`;
const buildLiveCardListCacheKey = (listScopeKey) => `${listScopeKey}|card_only`;
const isLiveMatchRow = (match = {}) => (
  match?.is_live === true
  || String(match?.status || '').trim().toUpperCase() === 'LIVE'
);
const isUserVisibleMatchRow = (match = {}) => (
  match?.is_visible !== false
  && match?.visible !== false
  && match?.user_visible !== false
  && match?.isUserVisible !== false
);
const isVisibleLiveMatchRow = (match = {}) => isLiveMatchRow(match) && isUserVisibleMatchRow(match);
const sliceLiveCardRows = (rows = [], page = 1, limit = 10) => {
  const normalizedPage = Math.max(1, Math.floor(Number(page) || 1));
  const normalizedLimit = Math.max(1, Math.floor(Number(limit) || 10));
  const start = (normalizedPage - 1) * normalizedLimit;
  return rows.slice(start, start + normalizedLimit);
};

const isSocketMatchHiddenPayload = (payload = {}) => (
  payload?.is_visible === false
  || payload?.visible === false
  || payload?.user_visible === false
  || payload?.isUserVisible === false
  || payload?.removeFixture === true
  || payload?.remove_fixture === true
  || payload?.consumerAction === 'remove_fixture'
  || payload?.consumer_action === 'remove_fixture'
);

const buildLiveShellSnapshotFromSocket = (payload = {}, options = {}) => {
  const matchId = payload?.match_id ?? payload?.matchId ?? payload?.id ?? options.matchId;
  return {
    id: matchId,
    match_id: matchId,
    ...payload,
    ...(payload?.authority_ts ? { authority_ts: payload.authority_ts } : {}),
    ...(payload?.authorityTs ? { authorityTs: payload.authorityTs } : {}),
    ...(payload?.sequence !== undefined ? { sequence: payload.sequence } : {}),
  };
};

const normalizeSportsnewFixturePatchEnvelope = (raw = {}) => {
  const patch = raw?.patch ?? raw;
  const fixtureId = raw?.fixtureId ?? raw?.fixture_id ?? patch?.fixtureId ?? patch?.fixture_id;
  return {
    sequence: raw?.sequence ?? patch?.sequence,
    emittedAt: raw?.emittedAt ?? raw?.emitted_at ?? patch?.emittedAt ?? patch?.emitted_at,
    patch: {
      ...patch,
      ...(fixtureId != null ? { fixtureId } : {}),
    },
  };
};

const sportsnewOddPatchForMarketsPatch = (odd = {}, patch = {}) => {
  const oddStateId = odd.oddStateId ?? odd.odd_state_id ?? odd.id;
  return {
    ...odd,
    ...(oddStateId != null ? { id: oddStateId, odds_id: oddStateId } : {}),
    odds: odd.odds ?? odd.priceDecimal ?? odd.price_decimal ?? odd.displayPrice ?? odd.display_price ?? null,
    is_suspended: Boolean(odd.is_suspended ?? odd.suspended ?? odd.locked ?? (odd.bettable === false)),
    target: {
      fixtureId: patch.fixtureId ?? patch.fixture_id,
      marketId: odd.marketTypeKey ?? odd.market_type_key ?? odd.marketId ?? odd.market_id,
      productMarketId: odd.marketGroupId ?? odd.market_group_id ?? odd.productMarketId ?? odd.product_market_id,
      lineId: odd.lineGroupId ?? odd.line_group_id ?? odd.lineId ?? odd.line_id ?? odd.canonicalLineKey ?? odd.canonical_line_key,
      selectionId: odd.selectionKey ?? odd.selection_key ?? odd.canonicalSelectionKey ?? odd.canonical_selection_key,
    },
  };
};

export const SportsProvider = ({ children }) => {
  const { services: { SportsService, SportsnewService }, session } = useServiceContextHook();
  const location = useLocation();
  const sportsSocketAuthToken = session?.user?.token || getStoredAuthToken() || '';
  const shouldConnectSportsSocket = Boolean(session?.user || sportsSocketAuthToken);

  /** 카트 배당 동기화 (SportsBetCartContext에서 가져옴 — 옵셔널) */
  const cartCtxRef = React.useRef(null);
  React.useEffect(() => {
    /** 카트 컨텍스트의 updateOddsInCart를 ref로 전달받기 위한 이벤트 리스너 */
    const handler = (e) => { if (e.detail?.updateOddsInCart) cartCtxRef.current = e.detail.updateOddsInCart; };
    window.addEventListener('sp_cart_sync', handler);
    return () => window.removeEventListener('sp_cart_sync', handler);
  }, []);

  const [categories, setCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [selectedSport, setSelectedSport] = useState(() =>
    buildInitialSelectedSport(initialSelectedSportId)
  );
  const [leagues, setLeagues] = useState([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);
  const [matches, setMatches] = useState([]);
  const [matchPage, setMatchPage] = useState(initialRouteMatchPage || 1);
  const [pendingMatchPage, setPendingMatchPage] = useState(null);
  const [matchTotal, setMatchTotal] = useState(0);
  const [matchTotalScopeKey, setMatchTotalScopeKey] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const DEFAULT_MATCH_LIMIT = 20;
  const LIVE_MATCH_LIMIT = 10;
  const [loading, setLoading] = useState(false);
  /** 베팅 타입: DOMESTIC, INTERNATIONAL, SPECIAL, LIVE */
  const [bettingType, setBettingType] = useState(
    () => initialBettingType
  );
  /** 검색어 (백엔드 파라미터) */
  const [searchQuery, setSearchQuery] = useState('');
  /** 정렬 순서 (백엔드 파라미터) */
  const [sortOrder, setSortOrder] = useState('ASC');
  const [liveCount, setLiveCount] = useState(0);
  const [matchCounts, setMatchCounts] = useState({ counts: [], total: 0 });
  /** Permanently true — all sport pages now use /sportsnew, no old API calls needed */
  const [pauseOldApiFetches, setPauseOldApiFetches] = useState(true);
  /** 주요경기 모드 */
  const [popularMode, setPopularMode] = useState(
    () => Boolean(initialSportsPageState?.popularMode)
  );
  /** 배당 변동 애니메이션 상태 { [odds_id]: 'up' | 'down' } */
  const [oddsFlash, setOddsFlash] = useState({});
  /** 최신 배당 override map — 상세 패널 stale 보정용 */
  const [oddsLatest, setOddsLatest] = useState({});
  const oddsLatestRef = useRef({});
  useEffect(() => { oddsLatestRef.current = oddsLatest; }, [oddsLatest]);
  /** 최신 마켓 suspend override map */
  const [marketLatest, setMarketLatest] = useState({});
  /** LIVE WS projection market tree — 기준점/selection 구조 교체용 */
  const [liveMarketProjectionByMatchId, setLiveMarketProjectionByMatchId] = useState({});
  const liveMarketProjectionByMatchIdRef = useRef({});
  useEffect(() => { liveMarketProjectionByMatchIdRef.current = liveMarketProjectionByMatchId; }, [liveMarketProjectionByMatchId]);
  /** LIVE WS fixture state — 점수/쿼터/시간 detail 보정용 */
  const [liveMatchStateByMatchId, setLiveMatchStateByMatchId] = useState({});
  const displayOrderingGuardRef = useRef(createRealtimeOrderingGuard());
  const [socketConnected, setSocketConnected] = useState(false);
  const lastWsEventAtRef = useRef(0);
  const [lastSocketConnectedAt, setLastSocketConnectedAt] = useState(0);
  const [socketRecoveryNonce, setSocketRecoveryNonce] = useState(0);
  const [lastSubscribeAckAt, setLastSubscribeAckAt] = useState(0);
  const [sportsSocketDebug, setSportsSocketDebug] = useState({
    requested: 0,
    subscribed: 0,
    lastAckAt: 0,
    ackTimedOutAt: 0,
  });
  /** 상세 페이지 match 구독 (SportsMatch 등 독립 페이지용) */
  const [detailMatchIds, setDetailMatchIds] = useState([]);
  const [liveCartMatchIds, setLiveCartMatchIds] = useState([]);
  const [terminalLiveMatchIds, setTerminalLiveMatchIds] = useState([]);
  /** display 설정 (odds format 등) */
  const [displaySettings, setDisplaySettings] = useState({ default_odds_format: 'decimal' });
  const socketRef = useRef(null);
  const flashTimerRef = useRef({});
  /** 구독 ID 목록 ref — connect/reconnect 시 stale closure 방지 */
  const matchesRef = useRef([]);
  const detailMatchIdsRef = useRef([]);
  const cartMatchIdsRef = useRef([]);
  const subscribedRoomsRef = useRef(new Set());
  const subscribedSubscriptionByRoomRef = useRef(new Map());
  const subscribedSportsnewFixtureRoomsRef = useRef(new Map());
  const subscriptionKeyRef = useRef('');
  const subscribeAckTimerRef = useRef(null);
  const previousBettingTypeRef = useRef(initialBettingType);
  const liveMatchListCacheRef = useRef(new Map());
  const liveMatchListInFlightRef = useRef(new Map());
  const liveCardListCacheRef = useRef(new Map());
  const liveCardListInFlightRef = useRef(new Map());
  const liveListRefreshTimerRef = useRef(null);
  const previousListQueryResetKeyRef = useRef('');
  const previousLiveRoutePageRef = useRef(initialRouteMatchPage || 1);

  const markWsEventReceived = useCallback((meta = {}) => {
    const now = Date.now();
    lastWsEventAtRef.current = now;
    markInplayV2BrowserDebug({
      lastSocketReceivedAt: now,
      ...meta,
    });
  }, []);

  const getLastWsEventAt = useCallback(() => lastWsEventAtRef.current, []);

  const clearSubscribeAckTimer = useCallback(() => {
    if (subscribeAckTimerRef.current) {
      clearTimeout(subscribeAckTimerRef.current);
      subscribeAckTimerRef.current = null;
    }
  }, []);

  const scheduleSubscribeAckTimeout = useCallback((expectedCount = 0) => {
    clearSubscribeAckTimer();
    if (!expectedCount) return;
    subscribeAckTimerRef.current = setTimeout(() => {
      const now = Date.now();
      setSportsSocketDebug((prev) => ({
        ...prev,
        ackTimedOutAt: now,
      }));
      setSocketRecoveryNonce((value) => value + 1);
    }, 5000);
  }, [clearSubscribeAckTimer]);

  const isKnownLiveSocketPayload = useCallback((payload = {}) => {
    const matchId = getSocketPayloadMatchId(payload);
    if (matchId == null) return false;
    return (
      matchesRef.current.some((match) => isSameSocketId(match?.id, matchId))
      || detailMatchIdsRef.current.some((id) => isSameSocketId(id, matchId))
      || cartMatchIdsRef.current.some((id) => isSameSocketId(id, matchId))
    );
  }, []);

  /** 공용 구독 함수: 현재 match ID 목록을 모아서 subscribe */
  const emitSubscriptions = useCallback((force = false) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    const ids = [
      ...matchesRef.current.map(m => m.id),
      ...detailMatchIdsRef.current,
      ...cartMatchIdsRef.current,
    ];
    const subscriptions = buildSocketMatchSubscriptions(ids, bettingType);
    const sportsnewFixtureSubscriptions = buildSportsnewFixtureSubscriptions(ids, bettingType);
    const diff = buildInplaySocketSubscriptionDiff({
      currentRooms: subscribedRoomsRef.current,
      desiredSubscriptions: subscriptions,
      bettingType,
      force,
    });

    if (!force && subscriptionKeyRef.current === diff.key) return;
    subscriptionKeyRef.current = diff.key;

    diff.unsubscribe.forEach((room) => {
      const subscription = subscribedSubscriptionByRoomRef.current.get(room);
      if (subscription) socket.emit('unsubscribe:match', subscription);
    });

    if (diff.subscribe.length > 0) {
      socket.emit('subscribe:matches', diff.subscribe);
      scheduleSubscribeAckTimeout(diff.subscribe.length);
    }

    subscribedRoomsRef.current = new Set(diff.desiredRooms);
    subscribedSubscriptionByRoomRef.current = new Map(
      diff.desiredRooms.map((room) => [
        room,
        subscriptions.find((subscription) => (
          buildInplaySocketSubscriptionKey(subscription, bettingType) === room
        )),
      ])
    );

    const desiredSportsnewFixtureRooms = new Map(
      sportsnewFixtureSubscriptions.map((subscription) => [
        sportsnewFixtureSubscriptionKey(subscription),
        subscription,
      ])
    );
    subscribedSportsnewFixtureRoomsRef.current.forEach((subscription, room) => {
      if (!desiredSportsnewFixtureRooms.has(room)) {
        socket.emit('unsubscribe:sportsnew:fixture', subscription);
      }
    });
    desiredSportsnewFixtureRooms.forEach((subscription, room) => {
      if (force || !subscribedSportsnewFixtureRoomsRef.current.has(room)) {
        socket.emit('subscribe:sportsnew:fixture', subscription);
      }
    });
    subscribedSportsnewFixtureRoomsRef.current = desiredSportsnewFixtureRooms;

    setSportsSocketDebug((prev) => ({
      ...prev,
      requested: diff.desiredRooms.length,
      subscribed: diff.desiredRooms.length - diff.subscribe.length,
    }));
  }, [bettingType, scheduleSubscribeAckTimeout]);

  const handleSubscribeAck = useCallback((ack = {}) => {
    const now = Date.now();
    clearSubscribeAckTimer();
    const subscribed = Number(ack?.subscribed ?? ack?.acked_match_count ?? 0);
    const requested = Number(ack?.requested ?? ack?.requested_count ?? subscribed);
    setLastSubscribeAckAt(now);
    setSportsSocketDebug((prev) => ({
      ...prev,
      requested: Number.isFinite(requested) ? requested : prev.requested,
      subscribed: Number.isFinite(subscribed) ? subscribed : prev.subscribed,
      lastAckAt: now,
    }));
  }, [clearSubscribeAckTimer]);

  /** 상세 페이지 match 구독 등록/해제 */
  const registerDetailMatch = useCallback((matchId) => {
    setDetailMatchIds(prev => prev.includes(matchId) ? prev : [...prev, matchId]);
  }, []);
  const unregisterDetailMatch = useCallback((matchId) => {
    setDetailMatchIds(prev => prev.filter(id => id !== matchId));
  }, []);

  const collectMatchMarketRefs = useCallback((matchId) => {
    const normalizedMatchId = Number(matchId);
    const refs = {
      oddsIds: new Set(),
      marketKeys: new Set(),
    };
    if (!normalizedMatchId) return refs;

    const mergeRefs = (markets) => {
      const collected = collectInplayMarketTreeRefs(markets);
      collected.oddsIds.forEach((id) => refs.oddsIds.add(String(id)));
      collected.marketKeys.forEach((key) => refs.marketKeys.add(String(key)));
    };

    const match = (matchesRef.current || []).find((row) => Number(row?.id ?? row?.match_id) === normalizedMatchId);
    mergeRefs(match?.markets);
    mergeRefs(liveMarketProjectionByMatchIdRef.current?.[String(normalizedMatchId)]?.markets);
    return refs;
  }, []);

  const clearInplayMatchMarkets = useCallback((matchId, options = {}) => {
    const normalizedMatchId = Number(matchId);
    if (!normalizedMatchId) return;
    const source = options.source || 'inplay_clear';
    const now = Date.now();
    const refs = collectMatchMarketRefs(normalizedMatchId);

    if (refs.oddsIds.size > 0) {
      setOddsLatest(prev => {
        const next = { ...prev };
        refs.oddsIds.forEach((id) => { delete next[id]; });
        return next;
      });
    }

    if (refs.marketKeys.size > 0) {
      setMarketLatest(prev => {
        const next = { ...prev };
        refs.marketKeys.forEach((key) => { delete next[key]; });
        return next;
      });
    }

    setMatches(prev => prev.map(match => {
      if (!isSameSocketId(match?.id ?? match?.match_id, normalizedMatchId)) return match;
      return {
        ...match,
        markets: [],
        ...(options.hidden === true ? { is_visible: false } : {}),
        ...(options.is_live === false ? { is_live: false } : {}),
        ...(options.status ? { status: options.status } : {}),
      };
    }));

    setLiveMarketProjectionByMatchId(prev => ({
      ...prev,
      [String(normalizedMatchId)]: {
        markets: [],
        authority_ts: options.authority_ts || null,
        sequence: options.sequence ?? null,
        projectionScope: 'full',
        isFullFixtureProjection: true,
        updatedAt: now,
        source,
      },
    }));

    markInplayV2BrowserDebug({
      lastStaleMarketsClearedAt: now,
      lastStaleMarketsClearedMatchId: normalizedMatchId,
      lastStaleMarketsClearSource: source,
      lastClearedOddsCount: refs.oddsIds.size,
      lastClearedMarketCount: refs.marketKeys.size,
    });
  }, [collectMatchMarketRefs]);

  const syncLiveMatchShellFromSnapshot = useCallback((snapshot, options = {}) => {
    const matchId = Number(snapshot?.id ?? snapshot?.match_id ?? snapshot?.matchId ?? options.matchId);
    if (!matchId || !hasLiveScoreShell(snapshot)) return false;

    const now = Date.now();
    const source = options.source || 'rest_detail_shell';
    const requestStartedAt = Number(options.requestStartedAt || 0);
    const shellPatch = buildLiveScoreShellPatch({
      ...snapshot,
      ...(options.authority_ts ? { authority_ts: options.authority_ts } : {}),
      ...(options.sequence !== undefined ? { sequence: options.sequence } : {}),
    });
    const shellSource = { id: matchId, ...shellPatch };
    const nextMeta = {
      updatedAt: now,
      source,
      _liveShellUpdatedAt: now,
      _liveShellSource: source,
    };
    const hasScorePatch = (
      shellSource.live_score_home !== undefined
      || shellSource.live_score_away !== undefined
    );

    setLiveMatchStateByMatchId(prev => {
      const key = String(matchId);
      const current = prev[key] || {};
      if (shouldPreserveLiveScoreShell(current, shellSource, { requestStartedAt })) return prev;
      const nextEntry = mergeLiveScoreShellFields(
        { id: matchId, ...current },
        shellSource,
        nextMeta,
      );
      return {
        ...prev,
        [key]: nextEntry,
      };
    });

    setMatches(prev => prev.map(match => {
      if (!isSameSocketId(match?.id ?? match?.match_id, matchId)) return match;
      if (shouldPreserveLiveScoreShell(match, shellSource, { requestStartedAt })) return match;
      const scoreChanged = (
        (shellSource.live_score_home !== undefined && match.live_score_home !== shellSource.live_score_home)
        || (shellSource.live_score_away !== undefined && match.live_score_away !== shellSource.live_score_away)
      );
      return mergeLiveScoreShellFields(match, shellSource, {
        ...nextMeta,
        ...(scoreChanged ? { _scoreFlash: true } : {}),
      });
    }));

    const incomingOrdering = normalizeRealtimeOrderingInfo(shellSource);
    if (incomingOrdering.hasOrderingSignal) {
      commitRealtimeOrderingGuard(displayOrderingGuardRef.current, 'match', matchId, shellSource);
    }

    markInplayV2BrowserDebug({
      lastScoreStatusUpdateReceivedAt: now,
      lastScoreStatusUpdateMatchId: matchId,
      lastScoreStatusUpdateSource: source,
    });
    const markRendered = () => markInplayV2BrowserDebug({
      lastScoreStatusUpdateRenderedAt: Date.now(),
      lastRenderedAt: Date.now(),
      lastRenderAt: Date.now(),
      browserRenderAt: Date.now(),
      lastScoreStatusUpdateRenderDelayMs: Date.now() - now,
      lastScoreStatusUpdateMatchId: matchId,
      lastScoreStatusUpdateSource: source,
    });
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(markRendered);
    } else {
      setTimeout(markRendered, 0);
    }

    if (hasScorePatch) {
      setTimeout(() => {
        setMatches(prev => prev.map(match => (
          isSameSocketId(match?.id ?? match?.match_id, matchId)
            ? { ...match, _scoreFlash: false }
            : match
        )));
      }, 1500);
    }

    return true;
  }, []);

  /** ref 동기화 — 최신 상태를 ref에 반영 */
  useEffect(() => { matchesRef.current = matches; }, [matches]);
  useEffect(() => { detailMatchIdsRef.current = detailMatchIds; }, [detailMatchIds]);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleCartMatchIds = (event) => {
      const ids = Array.isArray(event?.detail?.matchIds) ? event.detail.matchIds : [];
      cartMatchIdsRef.current = ids;
      setLiveCartMatchIds(ids);
      emitSubscriptions();
    };
    window.addEventListener('sp_cart_match_ids', handleCartMatchIds);
    return () => window.removeEventListener('sp_cart_match_ids', handleCartMatchIds);
  }, [emitSubscriptions]);

  /* ─── REST: 종목 로드 ─── */
  const fetchCategories = useCallback(async () => {
    try {
      const res = await SportsService.getCategories();
      if (res?.status) {
        setCategories(toArray(res.data));
      }
    } finally {
      setCategoriesLoaded(true);
    }
  }, [SportsService]);

  /* ─── REST: 리그 로드 ─── */
  const fetchLeagues = useCallback(async () => {
    if (!selectedSport?.slug) {
      setLeagues([]);
      setSelectedLeagueId(null);
      return;
    }
    const res = await SportsService.getLeagues(selectedSport.slug);
    if (res?.status) setLeagues(toArray(res.data, 'leagues'));
  }, [SportsService, selectedSport]);

  const matchLimit = bettingType === 'LIVE' ? LIVE_MATCH_LIMIT : DEFAULT_MATCH_LIMIT;
  const routeLiveSportId = useMemo(() => {
    if (location.pathname !== '/sports' || bettingType !== 'LIVE') return null;
    return getSportsRouteSportId(location.search);
  }, [bettingType, location.pathname, location.search]);
  const effectiveSelectedSport = useMemo(() => {
    if (bettingType !== 'LIVE' || !routeLiveSportId) return selectedSport;
    return categories.find((category) => Number(category?.id) === routeLiveSportId)
      || buildInitialSelectedSport(routeLiveSportId);
  }, [bettingType, categories, routeLiveSportId, selectedSport]);
  const selectedSportId = effectiveSelectedSport?.id ?? null;
  const hasResolvedSelectedSport =
    !selectedSportId
    || (effectiveSelectedSport?.slug && categories.some((category) => Number(category?.id) === Number(selectedSportId)));
  const canFetchLiveMatchesEarly = bettingType === 'LIVE' && Number.isFinite(Number(selectedSportId));
  const canFetchMatches = !pauseOldApiFetches && ((categoriesLoaded && hasResolvedSelectedSport) || canFetchLiveMatchesEarly);
  const listQueryResetKey = useMemo(() => JSON.stringify({
    bettingType,
    /**
     * LIVE도 selectedSport 기준으로 서버 pagination/count를 맞춘다.
     * 이전에는 all-sports page를 받은 뒤 client-side로만 종목 필터를 걸어
     * page는 10개 기준인데 화면에는 2~4개만 남는 현상이 있었다.
     */
    sportId: selectedSportId,
    leagueId: bettingType === 'LIVE' ? null : selectedLeagueId ?? null,
    search: searchQuery.trim(),
    sortOrder: bettingType === 'INTERNATIONAL' ? sortOrder : null,
    popularOnly: bettingType !== 'LIVE' ? popularMode : false,
    limit: matchLimit,
  }), [
    bettingType,
    selectedSportId,
    selectedLeagueId,
    searchQuery,
    sortOrder,
    popularMode,
    matchLimit,
  ]);
  const isMatchTotalCurrent = matchTotalScopeKey === listQueryResetKey;
  const scopedMatchTotal = bettingType === 'LIVE' && !isMatchTotalCurrent ? 0 : matchTotal;

  const mergeRowsWithDisplayGuard = useCallback((previousRows = [], incomingRows = [], options = {}) => {
    if (bettingType !== 'LIVE') return incomingRows;
    const previousById = new Map(
      previousRows.map((match) => [String(match?.id ?? match?.match_id ?? ''), match])
    );
    return incomingRows.map((row) => {
      const key = String(row?.id ?? row?.match_id ?? '');
      return mergeInplayMatchWithDisplayGuard(
        previousById.get(key),
        row,
        displayOrderingGuardRef.current,
        { source: options.source || 'rest' }
      );
    });
  }, [bettingType]);

  const applyLiveCardListPayload = useCallback((page, payload, source = 'rest_live_card_cache') => {
    const normalizedPage = Math.max(1, Math.floor(Number(page) || 1));
    const allRows = Array.isArray(payload?.rows) ? payload.rows : [];
    const total = allRows.length;
    const visibleRows = sliceLiveCardRows(allRows, normalizedPage, matchLimit);
    const requestStartedAt = Number(payload?.fetchedAt || Date.now());
    setMatches(prev => mergeRowsWithDisplayGuard(prev, visibleRows, { source }));
    setOddsLatest(prev => {
      const next = { ...prev };
      const now = Date.now();
      for (const m of visibleRows) eachDisplayedOdd(m, o => {
        next[o.id] = buildOddsLatestEntry(next[o.id], o, {
          now,
          requestStartedAt,
          source,
        });
      });
      return next;
    });
    setMatchPage(normalizedPage);
    setPendingMatchPage(null);
    setPageLoading(false);
    setMatchTotal(total);
    setMatchTotalScopeKey(listQueryResetKey);

    const numericSportId = Number(selectedSportId);
    if (!numericSportId) {
      setLiveCount(total);
      setMatchCounts(prev => ({
        ...(prev || {}),
        total,
        counts: Array.isArray(prev?.counts) ? prev.counts : [],
        _betting_category: 'LIVE',
      }));
    } else {
      setMatchCounts(prev => {
        if (prev?._betting_category && prev._betting_category !== 'LIVE') return prev;
        const counts = Array.isArray(prev?.counts) ? [...prev.counts] : [];
        const index = counts.findIndex((item) => Number(item?.sport_id) === numericSportId);
        const nextCount = {
          ...(index >= 0 ? counts[index] : {}),
          sport_id: numericSportId,
          count: total,
        };
        if (index >= 0) counts[index] = nextCount;
        else counts.push(nextCount);
        return {
          ...(prev || {}),
          counts,
          total: Number(prev?.total || 0),
          _betting_category: 'LIVE',
        };
      });
    }
    return visibleRows;
  }, [listQueryResetKey, matchLimit, mergeRowsWithDisplayGuard, selectedSportId]);

  /* ─── REST: 경기 로드 (레이스 컨디션 방지) ─── */
  const fetchSeqRef = useRef(0);
  const matchCountsFetchSeqRef = useRef(0);
  /**
   * 경기 목록 fetch.
   * @returns {Promise<Array>} 응답 rows (resync에서 활용). 레이스 가드로 stale 응답이면 빈 배열.
   */
  const fetchMatches = useCallback(async (page = 1, append = false, options = {}) => {
    const seq = ++fetchSeqRef.current;
    const forceRecoveryApply = options.forceCurrentOddsPath === true && bettingType !== 'LIVE';
    if (append) setLoadingMore(true);
    try {
      let params = { page, limit: matchLimit };
      if (selectedSportId) params.sport_id = selectedSportId;
      if (bettingType !== 'LIVE') {
        if (selectedLeagueId) params.league_id = selectedLeagueId;
      }
      if (bettingType) params.betting_category = bettingType;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (bettingType === 'INTERNATIONAL' && sortOrder) params.sort_order = sortOrder;
      if (popularMode && bettingType !== 'LIVE') params.popular_only = 'true';
      params = buildPrematchRecoveryRefreshParams(params, {
        forceCurrentOddsPath: options.forceCurrentOddsPath === true,
        bettingType,
      });

      const liveCardListCacheKey = bettingType === 'LIVE' && !append
        ? buildLiveCardListCacheKey(listQueryResetKey)
        : '';

      if (liveCardListCacheKey) {
        const cachedCardList = liveCardListCacheRef.current.get(liveCardListCacheKey);
        if (
          cachedCardList
          && options.forceLiveRequest
          && options.bypassLiveRecentCache !== true
          && Date.now() - cachedCardList.fetchedAt < 3000
        ) {
          return applyLiveCardListPayload(page, cachedCardList, 'rest_live_card_cache_recent_force');
        }

        if (options.forceLiveRequest && liveCardListInFlightRef.current.has(liveCardListCacheKey)) {
          const inFlightPayload = await liveCardListInFlightRef.current.get(liveCardListCacheKey);
          if (seq !== fetchSeqRef.current) return [];
          if (inFlightPayload) {
            return applyLiveCardListPayload(page, inFlightPayload, 'rest_live_card_inflight_force');
          }
        }

        if (options.forceLiveRequest) {
          liveCardListInFlightRef.current.delete(liveCardListCacheKey);
          liveCardListCacheRef.current.delete(liveCardListCacheKey);
        }

        if (
          cachedCardList
          && !options.forceLiveRequest
          && Date.now() - cachedCardList.fetchedAt < LIVE_CARD_LIST_CACHE_TTL_MS
        ) {
          return applyLiveCardListPayload(page, cachedCardList, 'rest_live_card_cache');
        }

        let cardPayload = null;
        if (!options.forceLiveRequest && liveCardListInFlightRef.current.has(liveCardListCacheKey)) {
          cardPayload = await liveCardListInFlightRef.current.get(liveCardListCacheKey);
        } else {
          const cardParams = {
            ...params,
            page: 1,
            limit: LIVE_CARD_LIST_LIMIT,
            card_only: 1,
          };
          const request = SportsService.getMatches(withLiveNoCacheParams(cardParams, bettingType))
            .then((response) => {
              if (!response?.status) return null;
              const rows = toArray(response.data);
              const payload = {
                fetchedAt: Date.now(),
                rows,
              };
              liveCardListCacheRef.current.set(liveCardListCacheKey, payload);
              return payload;
            })
            .catch(() => null)
            .finally(() => {
              if (liveCardListInFlightRef.current.get(liveCardListCacheKey) === request) {
                liveCardListInFlightRef.current.delete(liveCardListCacheKey);
              }
            });
          liveCardListInFlightRef.current.set(liveCardListCacheKey, request);
          cardPayload = await request;
        }

        if (seq !== fetchSeqRef.current) return [];
        if (cardPayload) {
          return applyLiveCardListPayload(page, cardPayload, 'rest_live_card');
        }
        return [];
      }

      if (bettingType === 'LIVE') {
        return [];
      }

      const liveCacheKey = bettingType === 'LIVE' && !append
        ? buildLiveMatchPageCacheKey(listQueryResetKey, page)
        : '';
      const cachedLivePage = liveCacheKey ? liveMatchListCacheRef.current.get(liveCacheKey) : null;
      if (cachedLivePage && Date.now() - cachedLivePage.fetchedAt < LIVE_MATCH_LIST_CACHE_TTL_MS) {
        const rows = cachedLivePage.rows || [];
        const total = cachedLivePage.total ?? rows.length;
        const cachedAt = cachedLivePage.fetchedAt || Date.now();
        setMatches(prev => mergeRowsWithDisplayGuard(prev, rows, { source: 'rest_cache' }));
        setOddsLatest(prev => {
          const next = { ...prev };
          const now = Date.now();
          for (const m of rows) eachDisplayedOdd(m, o => {
            next[o.id] = buildOddsLatestEntry(next[o.id], o, {
              now,
              requestStartedAt: cachedAt,
              source: 'rest_cache',
            });
          });
          return next;
        });
        setMatchPage(page);
        setPendingMatchPage(null);
        setMatchTotal(total);
        setMatchTotalScopeKey(listQueryResetKey);
        return rows;
      }

      const requestStartedAt = Date.now();
      let res = null;
      let sharedLivePayload = null;
      if (liveCacheKey && options.forceLiveRequest) {
        liveMatchListInFlightRef.current.delete(liveCacheKey);
        liveMatchListCacheRef.current.delete(liveCacheKey);
      }
      if (
        liveCacheKey
        && !options.foregroundLiveRequest
        && liveMatchListInFlightRef.current.has(liveCacheKey)
      ) {
        sharedLivePayload = await liveMatchListInFlightRef.current.get(liveCacheKey);
      } else {
        const request = SportsService.getMatches(withLiveNoCacheParams(params, bettingType));
        if (liveCacheKey) {
          const trackedRequest = request
            .then((response) => {
              if (!response?.status) return null;
              const rows = toArray(response.data);
              const total = response.data?.count ?? rows.length;
              const payload = {
                fetchedAt: Date.now(),
                rows,
                total,
              };
              liveMatchListCacheRef.current.set(liveCacheKey, payload);
              return payload;
            })
            .catch(() => null)
            .finally(() => {
              if (liveMatchListInFlightRef.current.get(liveCacheKey) === trackedRequest) {
                liveMatchListInFlightRef.current.delete(liveCacheKey);
              }
            });
          liveMatchListInFlightRef.current.set(liveCacheKey, trackedRequest);
        }
        res = await request;
      }
      // 레이스 가드: 이 응답이 최신 요청인 경우에만 반영
      if (seq !== fetchSeqRef.current) return [];
      const livePayload = sharedLivePayload;
      if (res?.status || livePayload) {
        const rows = livePayload ? (livePayload.rows || []) : toArray(res.data);
        const total = livePayload ? (livePayload.total ?? rows.length) : (res.data?.count ?? rows.length);
        if (append) {
          setMatches(prev => [
            ...prev,
            ...mergeRowsWithDisplayGuard(prev, rows, { source: 'rest_list_append' }),
          ]);
        } else {
          // polling/refetch flash: LIVE REST recovery is silent; WS owns live odds flash.
          const shouldFlashRestListOdds = bettingType !== 'LIVE';
          setMatches(prev => {
            if (prev.length > 0) {
              const _latestSnap = oddsLatestRef.current;
              const prevOddsMap = {};
              for (const m of prev) eachDisplayedOdd(m, o => {
                const prevOdds = getLatestOddsValue(_latestSnap, o.id, o.odds);
                if (prevOdds !== null) prevOddsMap[o.id] = prevOdds;
              });
              for (const m of rows) eachDisplayedOdd(m, o => {
                const now = Date.now();
                const latestEntry = _latestSnap?.[o.id];
                const preserveRealtime = forceRecoveryApply
                  ? false
                  : shouldPreserveRecentRealtimeOdds(latestEntry, o, {
                    now,
                    requestStartedAt,
                  });
                const prevVal = prevOddsMap[o.id];
                if (shouldFlashRestListOdds && !preserveRealtime && prevVal !== undefined) {
                  const newVal = toFiniteNumber(o.odds);
                  if (newVal !== null) {
                    const diff = Math.round((newVal - prevVal) * 100) / 100;
                    if (diff > 0.001) flashOdds(o.id, 'up', diff);
                    else if (diff < -0.001) flashOdds(o.id, 'down', Math.abs(diff));
                  }
                }
                if (!preserveRealtime && cartCtxRef.current) cartCtxRef.current(o.id, o.odds);
              });
            }
            return mergeRowsWithDisplayGuard(prev, rows, {
              source: forceRecoveryApply ? 'rest_force_resync_list' : 'rest_list',
            });
          });
        }
        // oddsLatest 동기화: polling/refetch 후 항상 최신 displayed odds 반영
        setOddsLatest(prev => {
          const next = { ...prev };
          const now = Date.now();
          for (const m of rows) eachDisplayedOdd(m, o => {
            next[o.id] = buildOddsLatestEntry(next[o.id], o, {
              now,
              requestStartedAt,
              source: forceRecoveryApply ? 'rest_force_resync_list' : 'rest_list',
              forceOverwrite: forceRecoveryApply,
            });
          });
          return next;
        });
        setMatchPage(page);
        setPendingMatchPage(null);
        setMatchTotal(total);
        setMatchTotalScopeKey(listQueryResetKey);
        if (bettingType === 'LIVE') {
          const numericTotal = Number(total) || rows.length || 0;
          const numericSportId = Number(selectedSportId);
          if (!numericSportId) {
            setLiveCount(numericTotal);
            setMatchCounts(prev => ({
              ...(prev || {}),
              total: numericTotal,
              counts: Array.isArray(prev?.counts) ? prev.counts : [],
              _betting_category: 'LIVE',
            }));
          } else {
            setMatchCounts(prev => {
              if (prev?._betting_category && prev._betting_category !== 'LIVE') return prev;
              const counts = Array.isArray(prev?.counts) ? [...prev.counts] : [];
              const index = counts.findIndex((item) => Number(item?.sport_id) === numericSportId);
              const nextCount = {
                ...(index >= 0 ? counts[index] : {}),
                sport_id: numericSportId,
                count: numericTotal,
              };
              if (index >= 0) counts[index] = nextCount;
              else counts.push(nextCount);
              return {
                ...(prev || {}),
                counts,
                total: Number(prev?.total || 0),
                _betting_category: 'LIVE',
              };
            });
          }
          if (!append && liveCacheKey) {
            liveMatchListCacheRef.current.set(liveCacheKey, {
              fetchedAt: Date.now(),
              rows,
              total,
            });
          }
        }
        return rows;
      }
      return [];
    } catch (e) {
      if (seq === fetchSeqRef.current) console.error('[fetchMatches] error:', e);
      return [];
    } finally {
      if (seq === fetchSeqRef.current && !append) setLoading(false);
      if (seq === fetchSeqRef.current && append) setLoadingMore(false);
      if (seq === fetchSeqRef.current && !append) {
        setPageLoading(false);
        setPendingMatchPage(null);
      }
    }
  }, [SportsService, selectedSportId, selectedLeagueId, bettingType, searchQuery, sortOrder, popularMode, matchLimit, listQueryResetKey, mergeRowsWithDisplayGuard, applyLiveCardListPayload]);

  const goToPage = useCallback((page, options = {}) => {
    const normalizedPage = Number(page);
    const isLivePageRequest = bettingType === 'LIVE';
    const forcePageRequest = options.force === true;
    const isSameActivePendingPage = pageLoading && normalizedPage === Number(pendingMatchPage);
    if (
      !Number.isFinite(normalizedPage)
      || normalizedPage < 1
      || (!forcePageRequest && !pageLoading && normalizedPage === Number(matchPage))
      || (!forcePageRequest && isSameActivePendingPage)
      || (!isLivePageRequest && loadingMore)
    ) return false;
    if (isLivePageRequest) {
      const liveCardListCacheKey = buildLiveCardListCacheKey(listQueryResetKey);
      const cachedCardList = liveCardListCacheRef.current.get(liveCardListCacheKey);
      const hasCachedCardRows = Array.isArray(cachedCardList?.rows) && cachedCardList.rows.length > 0;
      setPendingMatchPage(hasCachedCardRows ? null : normalizedPage);
      setPageLoading(!hasCachedCardRows);
      setMatchPage(normalizedPage);
      if (hasCachedCardRows) {
        applyLiveCardListPayload(normalizedPage, cachedCardList, 'rest_live_card_page_click_cache');
        if (
          options.deferLiveFetchToRoute !== true
          && Date.now() - cachedCardList.fetchedAt >= LIVE_CARD_LIST_CACHE_TTL_MS
        ) {
          fetchMatches(normalizedPage, false, { foregroundLiveRequest: true });
        }
        return true;
      }
    } else {
      setPendingMatchPage(normalizedPage);
      setPageLoading(true);
    }
    setMatchPage(normalizedPage);
    fetchMatches(normalizedPage, false, {
      forceLiveRequest: options.forceLiveRequest === true,
      foregroundLiveRequest: isLivePageRequest,
    });
    return true;
  }, [applyLiveCardListPayload, bettingType, fetchMatches, listQueryResetKey, loadingMore, matchPage, pageLoading, pendingMatchPage]);

  const livePendingPage = pageLoading && pendingMatchPage ? pendingMatchPage : null;
  const currentPage = matchPage;
  const totalPages = Math.max(1, Math.ceil(scopedMatchTotal / matchLimit));
  const hasMoreMatches = currentPage * matchLimit < scopedMatchTotal;
  const hasPrevPage = currentPage > 1;

  /* ─── REST: 라이브 경기 수 ─── */
  const fetchLiveCount = useCallback(async () => {
    try {
      const res = await SportsService.getLiveCount({ _live_ts: Date.now() });
      if (res?.status && res?.data) setLiveCount(res.data.live_count || 0);
    } catch (_) {}
  }, [SportsService]);


  /* ─── REST: 종목별 경기 수 ─── */
  const fetchMatchCounts = useCallback(async () => {
    const seq = ++matchCountsFetchSeqRef.current;
    const requestBettingType = bettingType;
    const requestPopularMode = popularMode;
    try {
      const params = {};
      if (requestBettingType) params.betting_category = requestBettingType;
      if (requestPopularMode && requestBettingType !== 'LIVE') params.popular_only = 'true';
      const res = await SportsService.getMatchCounts(withLiveNoCacheParams(params, requestBettingType));
      if (seq !== matchCountsFetchSeqRef.current) return;
      if (res?.status && res?.data) {
        setMatchCounts({
          ...res.data,
          _betting_category: requestBettingType,
        });
      }
    } catch (_) {}
  }, [SportsService, bettingType, popularMode]);

  const handleLiveListInvalidate = useCallback((data = {}) => {
    if (bettingType !== 'LIVE') return;
    if (location.pathname !== '/sports') return;

    const payloadSportId = Number(data?.sport_id ?? data?.sportId);
    const currentSportId = Number(selectedSportId);
    if (
      Number.isFinite(payloadSportId) &&
      Number.isFinite(currentSportId) &&
      payloadSportId > 0 &&
      currentSportId > 0 &&
      payloadSportId !== currentSportId
    ) {
      return;
    }

    const receivedAt = Date.now();
    markWsEventReceived({
      lastLiveListInvalidateReceivedAt: receivedAt,
      lastLiveListInvalidateReason: data?.reason || null,
      lastLiveListInvalidateMatchId: data?.match_id ?? data?.matchId ?? null,
      browserReceiveAt: receivedAt,
    });

    const invalidateMatchId = Number(data?.match_id ?? data?.matchId ?? data?.id);
    const shellSnapshot = buildLiveShellSnapshotFromSocket(data, { matchId: invalidateMatchId });
    const shellMerged = syncLiveMatchShellFromSnapshot(shellSnapshot, {
      matchId: invalidateMatchId,
      source: 'ws_live_list_invalidate_shell',
      authority_ts: data?.authority_ts,
      sequence: data?.sequence,
    });
    if (shellMerged && isHiddenLiveScoreShell(shellSnapshot)) {
      setTerminalLiveMatchIds((prev) => (
        prev.some((id) => isSameSocketId(id, invalidateMatchId)) ? prev : [...prev, invalidateMatchId]
      ));
      clearInplayMatchMarkets(invalidateMatchId, {
        source: 'ws_live_list_invalidate_hidden',
        hidden: true,
        is_live: false,
        status: shellSnapshot.status || shellSnapshot.fixture_status,
        authority_ts: data?.authority_ts,
        sequence: data?.sequence,
      });
      setMatches(prev => prev.filter(match => !isSameSocketId(match?.id ?? match?.match_id, invalidateMatchId)));
      markInplayV2BrowserDebug({
        lastLiveListInvalidateImmediateShellAt: receivedAt,
        lastLiveListInvalidateImmediateHiddenAt: Date.now(),
        lastLiveListInvalidateImmediateMatchId: invalidateMatchId,
      });
    } else if (shellMerged) {
      markInplayV2BrowserDebug({
        lastLiveListInvalidateImmediateShellAt: receivedAt,
        lastLiveListInvalidateImmediateMatchId: invalidateMatchId,
      });
    }

    if (liveListRefreshTimerRef.current) {
      clearTimeout(liveListRefreshTimerRef.current);
    }

    liveListRefreshTimerRef.current = setTimeout(() => {
      liveListRefreshTimerRef.current = null;
      const liveCardListCacheKey = buildLiveCardListCacheKey(listQueryResetKey);
      liveCardListCacheRef.current.delete(liveCardListCacheKey);
      liveCardListInFlightRef.current.delete(liveCardListCacheKey);

      fetchMatches(matchPage || 1, false, {
        forceLiveRequest: true,
        bypassLiveRecentCache: true,
      });
      fetchMatchCounts();
      fetchLiveCount();

      markInplayV2BrowserDebug({
        lastLiveListInvalidateRenderedAt: Date.now(),
        lastRenderedAt: Date.now(),
        lastRenderAt: Date.now(),
        browserRenderAt: Date.now(),
        lastLiveListInvalidateDelayMs: Date.now() - receivedAt,
      });
    }, LIVE_LIST_INVALIDATE_DEBOUNCE_MS);
  }, [
    bettingType,
    fetchLiveCount,
    fetchMatchCounts,
    fetchMatches,
    listQueryResetKey,
    location.pathname,
    markWsEventReceived,
    matchPage,
    selectedSportId,
    clearInplayMatchMarkets,
    syncLiveMatchShellFromSnapshot,
  ]);


  /* ─── REST: display 설정 로드 (1회) ─── */
  const fetchDisplaySettings = useCallback(async () => {
    try {
      const res = await SportsService.getDisplaySettings();
      if (res?.status && res?.data) setDisplaySettings(res.data);
    } catch (_) {}
  }, [SportsService]);

  /* ─── 초기 REST 로드 ─── */
  useEffect(() => {
    // Skipped: all sports pages use /sportsnew — SportsnewContext handles these
    if (pauseOldApiFetches) return;
    setLoading(true);
    Promise.all([fetchCategories(), fetchLiveCount(), fetchDisplaySettings()]).catch(() => {});
  }, [pauseOldApiFetches, fetchCategories, fetchLiveCount, fetchDisplaySettings]);

  useEffect(() => { fetchLeagues(); }, [fetchLeagues]);
  useEffect(() => {
    if (!categoriesLoaded) return;

    if (categories.length === 0) {
      setSelectedSport(null);
      return;
    }

    const currentSportId = Number(selectedSport?.id) || null;
    const matchedSport = currentSportId
      ? categories.find((category) => Number(category?.id) === currentSportId)
      : null;

    if (!currentSportId) return;

    if (!matchedSport) {
      setSelectedSport(null);
      return;
    }

    if (
      Number(selectedSport?.id) !== Number(matchedSport.id)
      || !selectedSport?.slug
    ) {
      setSelectedSport(matchedSport);
    }
  }, [categoriesLoaded, categories, selectedSport]);
  useEffect(() => {
    const previousBettingType = previousBettingTypeRef.current;
    previousBettingTypeRef.current = bettingType;
    if (previousBettingType === 'LIVE' && bettingType !== 'LIVE') {
      setPopularMode(false);
      setSelectedSport(null);
      setPendingMatchPage(null);
      setPageLoading(false);
      liveMatchListCacheRef.current.clear();
      liveMatchListInFlightRef.current.clear();
      liveCardListCacheRef.current.clear();
      liveCardListInFlightRef.current.clear();
      return;
    }
    if (bettingType !== 'LIVE' || previousBettingType === 'LIVE') return;

    setPopularMode(false);
    setSelectedSport(null);
  }, [bettingType, categories]);
  useEffect(() => {
    writeSportsPageState({
      bettingType,
      selectedSportId,
      popularMode: bettingType === 'LIVE' ? false : popularMode,
    });
  }, [bettingType, selectedSportId, popularMode]);
  useEffect(() => {
    if (location.pathname !== '/sports') return;
    const routeBettingType = getSportsRouteBettingType(location.search);
    if (!routeBettingType || routeBettingType === bettingType) return;

    setBettingType(routeBettingType);
    setPopularMode(false);
    if (routeBettingType === 'LIVE') {
      const liveSportId = getSportsRouteSportId(location.search);
      const routeLiveSport = liveSportId
        ? (categories.find((category) => Number(category?.id) === liveSportId)
          || buildInitialSelectedSport(liveSportId))
        : null;
      setSelectedSport(routeLiveSport);
    } else {
      setSelectedSport(null);
    }
  }, [bettingType, categories, location.pathname, location.search]);
  useEffect(() => {
    if (location.pathname !== '/sports') return;
    if (bettingType !== 'LIVE') return;

    const routeSportId = getSportsRouteSportId(location.search);
    if (!routeSportId) return setSelectedSport(null);
    if (Number(selectedSport?.id) === routeSportId) return;

    const matchedSport = categories.find((category) => Number(category?.id) === routeSportId)
      || buildInitialSelectedSport(routeSportId);
    setSelectedSport(matchedSport);
  }, [bettingType, categories, location.pathname, location.search, selectedSport?.id]);
  /**
   * 목록 query가 바뀌는 즉시 이전 rows를 비워 stale-frame을 줄인다.
   * LIVE는 API가 느릴 때 0-count/빈 목록으로 튀는 느낌이 더 크므로 이전 rows를 유지한다.
   * 탭 전환뿐 아니라 mobile SPECIAL sport/popular chip 전환에서도
   * active chip/request만 먼저 바뀌고 board가 늦게 따라오는 짧은 잔상을 막기 위한 reset이다.
   */
  useEffect(() => {
    if (!canFetchMatches) return;
    const resetPage = bettingType === 'LIVE'
      ? (getSportsRoutePage(location.search) || 1)
      : 1;
    const previousQueryKey = previousListQueryResetKeyRef.current;
    const isFirstQuery = !previousQueryKey;
    const queryChanged = previousQueryKey && previousQueryKey !== listQueryResetKey;
    previousListQueryResetKeyRef.current = listQueryResetKey;
    fetchSeqRef.current += 1;
    if (bettingType === 'LIVE') {
      const previousRoutePage = previousLiveRoutePageRef.current;
      previousLiveRoutePageRef.current = resetPage;
      const isPageOnlyChange = !isFirstQuery && !queryChanged && previousRoutePage !== resetPage;
      const hasRows = matchesRef.current.length > 0;
      const liveCardListCacheKey = buildLiveCardListCacheKey(listQueryResetKey);
      const cachedCardList = liveCardListCacheRef.current.get(liveCardListCacheKey);
      const hasCachedCardRows = Array.isArray(cachedCardList?.rows) && cachedCardList.rows.length > 0;
      setLoading(!hasCachedCardRows && !isPageOnlyChange && (isFirstQuery || queryChanged || !hasRows));
      setPageLoading(!hasCachedCardRows && (isPageOnlyChange || queryChanged));
      setPendingMatchPage(hasCachedCardRows ? null : resetPage);
      if (hasCachedCardRows) {
        applyLiveCardListPayload(resetPage, cachedCardList, 'rest_live_card_route_cache');
      } else if (queryChanged || isFirstQuery) {
        setMatchTotalScopeKey('');
        setMatches([]);
      } else {
        setMatches(prev => prev.filter(isVisibleLiveMatchRow));
      }
    } else {
      setLoading(true);
      setPageLoading(false);
      setPendingMatchPage(null);
      setMatchTotalScopeKey('');
      setMatches([]);
      setMatchTotal(0);
    }
    setMatchPage(resetPage);
  }, [applyLiveCardListPayload, canFetchMatches, listQueryResetKey, bettingType, location.search]);
  useEffect(() => {
    if (!canFetchMatches) return;
    const routePage = bettingType === 'LIVE'
      ? (getSportsRoutePage(location.search) || 1)
      : 1;
    fetchMatches(routePage, false);
  }, [bettingType, canFetchMatches, fetchMatches, location.search]);
  useEffect(() => { if (!pauseOldApiFetches) fetchMatchCounts(); }, [pauseOldApiFetches, fetchMatchCounts]);
  useEffect(() => {
    if (bettingType !== 'LIVE' || !canFetchMatches) return undefined;
    const interval = setInterval(() => {
      if (isDocumentHidden()) return;
      fetchMatchCounts();
    }, LIVE_COUNT_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [bettingType, canFetchMatches, fetchMatchCounts, fetchLiveCount]);

  /* ─── 배당 플래시 애니메이션 헬퍼 ─── */
  const flashOdds = useCallback((oddsId, direction, delta, meta = {}) => {
    const now = Date.now();
    const changedSelection = {
      oddsId,
      direction,
      delta,
      changedSelectionIdentity: meta.changedSelectionIdentity ?? oddsId,
      oldPrice: meta.oldPrice,
      newPrice: meta.newPrice,
      flashedAt: now,
    };
    const previousDebug = (typeof window !== 'undefined' && window.__sportsInplayV2Debug) || {};
    const changedSelections = [
      changedSelection,
      ...((previousDebug.changedSelections || []).slice(0, 19)),
    ];
    markInplayV2BrowserDebug({
      lastFlashAt: now,
      lastOddsFlashAt: now,
      lastOddsFlash: changedSelection,
      lastChangedOdds: changedSelection,
      changedSelections,
      changedSelectionIdentity: meta.changedSelectionIdentity ?? oddsId,
      oldPrice: meta.oldPrice,
      newPrice: meta.newPrice,
      direction,
    });
    setOddsFlash(prev => ({ ...prev, [oddsId]: { direction, delta } }));
    /** 기존 타이머 클리어 */
    if (flashTimerRef.current[oddsId]) {
      clearTimeout(flashTimerRef.current[oddsId]);
    }
    /** 1초 후 플래시 제거 */
    flashTimerRef.current[oddsId] = setTimeout(() => {
      setOddsFlash(prev => {
        const next = { ...prev };
        delete next[oddsId];
        return next;
      });
    }, 1000);
  }, []);

  /* ─── 배당 업데이트 핸들러 ─── */
  const handleOddsUpdate = useCallback((data) => {
    if (!shouldAcceptSocketPayload(bettingType, data, isKnownLiveSocketPayload)) return;
    const { match_id, odds_id, odds, is_suspended } = data;
    const payloadOddsId = odds_id ?? data?.id;
    const receivedAt = Date.now();
    if (payloadOddsId == null) return;
    const incomingSelection = { ...data, id: payloadOddsId, odds, is_suspended };
    if (!shouldApplyRealtimeOrderingGuard(displayOrderingGuardRef.current, 'odd', payloadOddsId, incomingSelection)) return;
    commitRealtimeOrderingGuard(displayOrderingGuardRef.current, 'odd', payloadOddsId, incomingSelection);
    markWsEventReceived();

    const oldOdds = getLatestOddsValue(oddsLatestRef.current, payloadOddsId, undefined);
    const newOdds = toFiniteNumber(odds);
    if (newOdds !== null && oldOdds !== null) {
      if (newOdds > oldOdds) flashOdds(payloadOddsId, 'up', Math.round((newOdds - oldOdds) * 100) / 100);
      else if (newOdds < oldOdds) flashOdds(payloadOddsId, 'down', Math.round((oldOdds - newOdds) * 100) / 100);
    }

    /** 카트 배당 동기화 */
    const normalizedOdds = toFiniteNumber(odds);
    if (cartCtxRef.current && payloadOddsId != null && normalizedOdds !== null) {
      cartCtxRef.current(payloadOddsId, normalizedOdds, {
        ...(is_suspended !== undefined && { is_suspended }),
      });
    }

    /** 상세 패널 stale 보정 */
    if (payloadOddsId != null) {
      setOddsLatest(prev => ({
        ...prev,
        [payloadOddsId]: {
          ...(prev[payloadOddsId] || {}),
          ...buildOddsLatestEntry(prev[payloadOddsId], incomingSelection, {
            now: receivedAt,
            source: 'ws',
          }),
        },
      }));
    }
  }, [bettingType, flashOdds, isKnownLiveSocketPayload, markWsEventReceived]);

  /* ─── 배당 일괄 업데이트 핸들러 ─── */
  const handleOddsBulkUpdate = useCallback((data) => {
    if (!shouldAcceptSocketPayload(bettingType, data, isKnownLiveSocketPayload)) return;
    const { match_id, odds: oddsList } = data;
    if (!Array.isArray(oddsList)) return;
    const receivedAt = Date.now();
    const acceptedOddsList = oddsList.filter((row) => {
      const id = row?.odds_id ?? row?.id;
      if (id == null) return false;
      return shouldApplyRealtimeOrderingGuard(displayOrderingGuardRef.current, 'odd', id, row);
    });
    if (acceptedOddsList.length === 0) return;
    acceptedOddsList.forEach((row) => {
      const id = row?.odds_id ?? row?.id;
      commitRealtimeOrderingGuard(displayOrderingGuardRef.current, 'odd', id, row);
    });
    markWsEventReceived();

    acceptedOddsList.forEach(o => {
      const id = o?.odds_id ?? o?.id;
      if (id == null) return;
      const oldOdds = getLatestOddsValue(oddsLatestRef.current, id, undefined);
      const newOdds = toFiniteNumber(o.odds);
      if (newOdds !== null && oldOdds !== null) {
        if (newOdds > oldOdds) {
          flashOdds(id, 'up', Math.round((newOdds - oldOdds) * 100) / 100);
        } else if (newOdds < oldOdds) {
          flashOdds(id, 'down', Math.round((oldOdds - newOdds) * 100) / 100);
        }
      }
    });

    /** 카트 배당 일괄 동기화 */
    if (cartCtxRef.current) {
      acceptedOddsList.forEach(o => {
        const id = o?.odds_id ?? o?.id;
        const normalized = toFiniteNumber(o?.odds);
        if (id != null && normalized !== null) {
          cartCtxRef.current(id, normalized, {
            ...(o.is_suspended !== undefined && { is_suspended: o.is_suspended }),
          });
        }
      });
    }

    /** 상세 패널 + 메인 보드 stale 보정 (is_suspended 포함) */
    setOddsLatest(prev => {
      const next = { ...prev };
      acceptedOddsList.forEach(o => {
        const id = o?.odds_id ?? o?.id;
        if (id == null) return;
        next[id] = buildOddsLatestEntry(next[id], { ...o, id }, {
          now: receivedAt,
          source: 'ws',
        });
      });
      return next;
    });
  }, [bettingType, flashOdds, isKnownLiveSocketPayload, markWsEventReceived]);

  /* ─── LIVE 마켓 projection 교체 핸들러 (기준점/선택지 구조 변경) ─── */
  const handleMarketsReplace = useCallback((data) => {
    if (!shouldAcceptSocketPayload(bettingType, data, isKnownLiveSocketPayload)) return;
    const receivedAt = Date.now();
    const matchId = Number(data?.match_id ?? data?.matchId ?? data?.id);
    const markets = Array.isArray(data?.markets) ? data.markets : [];
    if (!Number.isFinite(matchId)) return;
    if (hasLiveScoreShell(data)) {
      syncLiveMatchShellFromSnapshot(buildLiveShellSnapshotFromSocket(data, { matchId }), {
        matchId,
        source: 'ws_markets_replace_shell',
        authority_ts: data?.authority_ts,
        sequence: data?.sequence,
      });
    }
    const projectionScope = String(data?.projectionScope || data?.projection_scope || '').trim().toLowerCase();
    const isFullFixtureProjection = (
      data?.isFullFixtureProjection === true
      || data?.is_full_fixture_projection === true
      || projectionScope === 'full'
    );
    const replaceReason = getMarketsReplaceReason(data);
    const changedKeyPayload = buildChangedKeyPayload(data);
    const preserveUnchangedOnFullProjection = isDiffFriendlyMarketsReplace(data, isFullFixtureProjection);
    const changedIdentityKeySet = buildInplayChangedKeySet(changedKeyPayload.changedIdentityKeys);
    markInplayV2BrowserDebug({
      lastMarketsReplaceReceivedAt: receivedAt,
      lastSocketReceivedAt: receivedAt,
      browserReceiveAt: receivedAt,
      lastMarketsReplaceAt: receivedAt,
      lastMarketsReplaceMatchId: matchId,
      lastMarketsReplaceMarketCount: markets.length,
      lastMarketsReplaceProjectionScope: projectionScope || 'unspecified',
      lastMarketsReplaceIsFullFixtureProjection: isFullFixtureProjection,
      lastMarketsReplaceReason: replaceReason || null,
      lastMarketsReplacePreserveUnchanged: preserveUnchangedOnFullProjection,
      lastMarketsReplaceChangedIdentityKeyCount: changedKeyPayload.changedIdentityKeys.length,
      lastMarketsReplaceChangedLineSetKeyCount: changedKeyPayload.changedLineSetKeys.length,
      lastMarketsReplaceChangedMarketKeyCount: changedKeyPayload.changedMarketKeys.length,
      lastMarketsReplaceHiddenCount: data?.hiddenCount ?? null,
      lastMarketsReplaceLockedCount: data?.lockedCount ?? null,
      lastMarketsReplaceOpenCount: data?.openCount ?? null,
      lastMarketsReplaceReasonCounts: data?.reasonCounts || null,
      lastMaterialTrace: data?.observe?.materialTrace || null,
    });
    const previousPricesByIdentity = new Map();
    const previousMatch = (matchesRef.current || []).find(match => isSameSocketId(match?.id, matchId));
    const previousSelectionsByIdentity = previousMatch
      ? collectDisplayedSelectionsByIdentity(previousMatch)
      : new Map();
    const incomingSelectionsByIdentity = collectDisplayedSelectionsByIdentity(markets);
    let labelOnlyUpdateCount = 0;
    incomingSelectionsByIdentity.forEach((incomingSelection, key) => {
      const previousSelection = previousSelectionsByIdentity.get(key);
      if (!previousSelection) return;
      if (getSelectionLabelSignature(previousSelection) === getSelectionLabelSignature(incomingSelection)) return;
      if (getSelectionStateSignature(previousSelection) !== getSelectionStateSignature(incomingSelection)) return;
      labelOnlyUpdateCount += 1;
    });
    const incomingIdentityKeys = new Set(incomingSelectionsByIdentity.keys());
    const mergePreservedCount = preserveUnchangedOnFullProjection
      ? countPreservedFullProjectionSelections({
        previousSelectionsByIdentity,
        incomingSelectionsByIdentity,
        changedIdentityKeySet,
      })
      : (
        isFullFixtureProjection
          ? 0
          : [...previousSelectionsByIdentity.keys()].filter((key) => !incomingIdentityKeys.has(key)).length
      );
    const removedByDisplayGuardCount = countExplicitDisplayHiddenSelections(markets);
    if (previousMatch) {
      eachDisplayedOdd(previousMatch, (selection) => {
        const key = getSelectionPriceIdentityKey(selection);
        const value = getLatestOddsValue(oddsLatestRef.current, selection.id, selection.odds);
        if (key && value !== null) previousPricesByIdentity.set(key, value);
      });
    }

    const incomingMatch = {
      id: matchId,
      match_id: matchId,
      markets,
      ...(data?.authority_ts ? { authority_ts: data.authority_ts } : {}),
      ...(data?.sequence !== undefined ? { sequence: data.sequence } : {}),
      ...(data?.projection_mode ? { projection_mode: data.projection_mode } : {}),
      ...(data?.projectionMode ? { projectionMode: data.projectionMode } : {}),
      ...(replaceReason ? { replaceReason } : {}),
      ...(projectionScope ? { projectionScope } : {}),
      ...changedKeyPayload,
      isFullFixtureProjection,
    };
    if (!shouldApplyRealtimeOrderingGuard(displayOrderingGuardRef.current, 'match', matchId, incomingMatch, {
      source: 'ws_inplay_market_projection',
    })) return;
    markWsEventReceived();

    if (markets.length === 0) {
      if (!isFullFixtureProjection) return;
      commitRealtimeOrderingGuard(displayOrderingGuardRef.current, 'match', matchId, incomingMatch, {
        source: 'ws_inplay_market_projection',
      });
      clearInplayMatchMarkets(matchId, {
        source: 'ws_markets_replace_empty',
        authority_ts: data?.authority_ts,
        sequence: data?.sequence,
      });
      const markRendered = () => markInplayV2BrowserDebug({
        lastMarketsReplaceRenderedAt: Date.now(),
        lastRenderedAt: Date.now(),
        lastRenderAt: Date.now(),
        browserRenderAt: Date.now(),
        lastMarketsReplaceRenderDelayMs: Date.now() - receivedAt,
        lastMarketsReplaceMatchId: matchId,
        lastApplyMode: 'full_empty_clear',
        lastMergePreservedCount: 0,
        lastRemovedByDisplayGuardCount: 0,
        lastLabelOnlyUpdateCount: 0,
        lastPriceFlashCount: 0,
        lastMaterialTrace: data?.observe?.materialTrace || null,
      });
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(markRendered);
      } else {
        setTimeout(markRendered, 0);
      }
      return;
    }

    let priceFlashCount = 0;
    markets.forEach(market => {
      eachMarketOdd(market, (selection) => {
        const key = getSelectionPriceIdentityKey(selection);
        const id = selection?.id ?? selection?.odds_id ?? selection?.oddsId;
        if (!key || id == null) return;
        if (preserveUnchangedOnFullProjection && changedIdentityKeySet.size > 0 && !hasChangedIdentityKey(changedIdentityKeySet, key)) {
          return;
        }
        const previousOdds = previousPricesByIdentity.get(key);
        const nextOdds = toFiniteNumber(selection?.odds);
        if (previousOdds === undefined || nextOdds === null) return;
        const diff = Math.round((nextOdds - previousOdds) * 100) / 100;
        if (diff > 0.001) {
          const meta = { changedSelectionIdentity: key, oldPrice: previousOdds, newPrice: nextOdds };
          flashOdds(id, 'up', diff, meta);
          if (key !== `id:${id}`) flashOdds(key, 'up', diff, meta);
          priceFlashCount += 1;
        } else if (diff < -0.001) {
          const meta = { changedSelectionIdentity: key, oldPrice: previousOdds, newPrice: nextOdds };
          flashOdds(id, 'down', Math.abs(diff), meta);
          if (key !== `id:${id}`) flashOdds(key, 'down', Math.abs(diff), meta);
          priceFlashCount += 1;
        }
      });
    });

    setMatches(prev => prev.map(match => {
      if (!isSameSocketId(match?.id, matchId)) return match;
      return mergeInplayMatchWithDisplayGuard(match, incomingMatch, displayOrderingGuardRef.current, {
        source: 'ws_inplay_market_projection',
        replaceMarkets: isFullFixtureProjection,
        replaceSelections: isFullFixtureProjection,
        preserveUnchangedOnFullProjection,
        ...changedKeyPayload,
      });
    }));

    setMarketLatest(prev => {
      const next = { ...prev };
      markets.forEach(market => {
        const marketId = getCustomerMarketKey(market);
        if (marketId == null) return;
        const nextSuspended = (
          isLockedFlag(market.is_suspended)
          || isLockedFlag(market.suspended)
          || isLockedFlag(market.betting_locked)
          || isLockedFlag(market.non_tradable)
        );
        next[marketId] = {
          ...(next[marketId] || {}),
          is_suspended: nextSuspended,
          updatedAt: Date.now(),
          source: 'ws_inplay_market_projection',
          ...(market.authority_ts ? { authority_ts: market.authority_ts } : {}),
          ...(market.sequence !== undefined ? { sequence: market.sequence } : {}),
        };
      });
      return next;
    });

    setOddsLatest(prev => {
      const next = { ...prev };
      markets.forEach(market => {
        eachMarketOdd(market, (selection) => {
          const key = getSelectionPriceIdentityKey(selection);
          if (preserveUnchangedOnFullProjection && changedIdentityKeySet.size > 0 && key && !hasChangedIdentityKey(changedIdentityKeySet, key)) {
            return;
          }
          const id = selection?.id ?? selection?.odds_id ?? selection?.oddsId;
          if (id == null) return;
          next[id] = buildOddsLatestEntry(next[id], { ...selection, id }, {
            now: Date.now(),
            source: 'ws_inplay_market_projection',
          });
        });
      });
      return next;
    });

    setLiveMarketProjectionByMatchId(prev => {
      const previousEntry = prev?.[String(matchId)] || null;
      const baseMarkets = Array.isArray(previousEntry?.markets)
        ? previousEntry.markets
        : previousMatch?.markets || [];
      const mergedMarkets = isFullFixtureProjection && !preserveUnchangedOnFullProjection
        ? markets
        : mergeInplayMatchWithDisplayGuard(
          { id: matchId, match_id: matchId, markets: baseMarkets },
          incomingMatch,
          displayOrderingGuardRef.current,
          {
            source: 'ws_inplay_market_projection',
            replaceMarkets: isFullFixtureProjection,
            replaceSelections: isFullFixtureProjection,
            preserveUnchangedOnFullProjection,
            ...changedKeyPayload,
          }
        )?.markets || markets;
      return {
        ...prev,
        [String(matchId)]: {
          markets: mergedMarkets,
          authority_ts: data?.authority_ts || null,
          sequence: data?.sequence ?? null,
          replaceReason,
          preserveUnchangedOnFullProjection,
          ...changedKeyPayload,
          projectionScope: projectionScope || (isFullFixtureProjection ? 'full' : 'partial'),
          isFullFixtureProjection,
          updatedAt: Date.now(),
          source: 'ws_inplay_market_projection',
        },
      };
    });
    const markRendered = () => markInplayV2BrowserDebug({
      lastMarketsReplaceRenderedAt: Date.now(),
      lastRenderedAt: Date.now(),
      lastRenderAt: Date.now(),
      browserRenderAt: Date.now(),
      lastMarketsReplaceRenderDelayMs: Date.now() - receivedAt,
      lastMarketsReplaceMatchId: matchId,
      lastApplyMode: preserveUnchangedOnFullProjection
        ? 'full_replace_diff_preserve'
        : (isFullFixtureProjection ? 'full_replace' : 'merge_preserve'),
      lastMarketsReplaceReason: replaceReason || null,
      lastMarketsReplacePreserveUnchanged: preserveUnchangedOnFullProjection,
      lastMergePreservedCount: mergePreservedCount,
      lastRemovedByDisplayGuardCount: removedByDisplayGuardCount,
      lastLabelOnlyUpdateCount: labelOnlyUpdateCount,
      lastPriceFlashCount: priceFlashCount,
      lastMaterialTrace: data?.observe?.materialTrace || null,
    });
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(markRendered);
    } else {
      setTimeout(markRendered, 0);
    }
  }, [bettingType, clearInplayMatchMarkets, flashOdds, isKnownLiveSocketPayload, markWsEventReceived, syncLiveMatchShellFromSnapshot]);

  /* ─── LIVE 마켓 delta patch 핸들러 (가격/잠금만, 구조 변경은 replace 권위 유지) ─── */
  const handleMarketsPatch = useCallback((data) => {
    if (!shouldAcceptSocketPayload(bettingType, data, isKnownLiveSocketPayload)) return;
    const receivedAt = Date.now();
    const matchId = Number(data?.match_id ?? data?.matchId ?? data?.id);
    const patches = Array.isArray(data?.patches) ? data.patches : [];
    if (!Number.isFinite(matchId)) return;
    if (hasLiveScoreShell(data)) {
      syncLiveMatchShellFromSnapshot(buildLiveShellSnapshotFromSocket(data, { matchId }), {
        matchId,
        source: 'ws_markets_patch_shell',
        authority_ts: data?.authority_ts,
        sequence: data?.sequence,
      });
    }
    if (patches.length === 0) return;

    const acceptedPatches = patches.filter((patch) => {
      const id = getInplayMarketsPatchOddId(patch);
      if (id == null) return true;
      const selection = buildInplayMarketsPatchSelection(patch, data);
      return shouldApplyRealtimeOrderingGuard(displayOrderingGuardRef.current, 'odd', id, selection);
    });
    if (acceptedPatches.length === 0) return;

    acceptedPatches.forEach((patch) => {
      const id = getInplayMarketsPatchOddId(patch);
      if (id == null) return;
      commitRealtimeOrderingGuard(
        displayOrderingGuardRef.current,
        'odd',
        id,
        buildInplayMarketsPatchSelection(patch, data)
      );
    });

    markWsEventReceived({
      lastMarketsPatchReceivedAt: receivedAt,
      browserReceiveAt: receivedAt,
      lastMarketsPatchMatchId: matchId,
      lastMarketsPatchCount: acceptedPatches.length,
      lastMaterialTrace: data?.observe?.materialTrace || null,
    });

    let priceFlashCount = 0;
    acceptedPatches.forEach((patch) => {
      const id = getInplayMarketsPatchOddId(patch);
      if (id == null) return;
      const selection = buildInplayMarketsPatchSelection(patch, data);
      const oldOdds = getLatestOddsValue(oddsLatestRef.current, id, undefined);
      const newOdds = toFiniteNumber(selection.odds);
      if (newOdds !== null && oldOdds !== null) {
        const diff = Math.round((newOdds - oldOdds) * 100) / 100;
        const identityKey = getSelectionPriceIdentityKey(selection) || id;
        const meta = { changedSelectionIdentity: identityKey, oldPrice: oldOdds, newPrice: newOdds };
        if (diff > 0.001) {
          flashOdds(id, 'up', diff, meta);
          if (identityKey !== `id:${id}`) flashOdds(identityKey, 'up', diff, meta);
          priceFlashCount += 1;
        } else if (diff < -0.001) {
          flashOdds(id, 'down', Math.abs(diff), meta);
          if (identityKey !== `id:${id}`) flashOdds(identityKey, 'down', Math.abs(diff), meta);
          priceFlashCount += 1;
        }
      }
      if (cartCtxRef.current && newOdds !== null) {
        cartCtxRef.current(id, newOdds, {
          ...(selection.is_suspended !== undefined && { is_suspended: selection.is_suspended }),
        });
      }
    });

    setOddsLatest(prev => {
      const next = { ...prev };
      acceptedPatches.forEach((patch) => {
        const id = getInplayMarketsPatchOddId(patch);
        if (id == null) return;
        const selection = buildInplayMarketsPatchSelection(patch, data);
        next[id] = buildOddsLatestEntry(next[id], selection, {
          now: receivedAt,
          source: 'ws_markets_patch',
        });
      });
      return next;
    });

    setMatches(prev => prev.map(match => {
      if (!isSameSocketId(match?.id, matchId)) return match;
      const result = applyInplayMarketsPatchToMarketTree(match?.markets || [], acceptedPatches, data);
      if (result.appliedCount === 0) return match;
      return {
        ...match,
        markets: result.markets,
      };
    }));

    setLiveMarketProjectionByMatchId(prev => {
      const key = String(matchId);
      const previousEntry = prev?.[key] || null;
      const previousMatch = (matchesRef.current || []).find(match => isSameSocketId(match?.id, matchId));
      const baseMarkets = Array.isArray(previousEntry?.markets)
        ? previousEntry.markets
        : previousMatch?.markets || [];
      const result = applyInplayMarketsPatchToMarketTree(baseMarkets, acceptedPatches, data);
      if (result.appliedCount === 0) return prev;
      return {
        ...prev,
        [key]: {
          ...(previousEntry || {}),
          markets: result.markets,
          authority_ts: data?.authority_ts || previousEntry?.authority_ts || null,
          sequence: data?.sequence ?? previousEntry?.sequence ?? null,
          projectionScope: data?.projectionScope || data?.projection_scope || previousEntry?.projectionScope || 'patch',
          isFullFixtureProjection: previousEntry?.isFullFixtureProjection === true,
          updatedAt: Date.now(),
          source: 'ws_markets_patch',
        },
      };
    });

    const markRendered = () => markInplayV2BrowserDebug({
      lastMarketsPatchRenderedAt: Date.now(),
      lastRenderedAt: Date.now(),
      lastRenderAt: Date.now(),
      browserRenderAt: Date.now(),
      lastMarketsPatchRenderDelayMs: Date.now() - receivedAt,
      lastMarketsPatchMatchId: matchId,
      lastMarketsPatchAppliedCount: acceptedPatches.length,
      lastMarketsPatchPriceFlashCount: priceFlashCount,
      lastMaterialTrace: data?.observe?.materialTrace || null,
    });
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(markRendered);
    } else {
      setTimeout(markRendered, 0);
    }
  }, [bettingType, flashOdds, isKnownLiveSocketPayload, markWsEventReceived, syncLiveMatchShellFromSnapshot]);

  /* ─── 라이브 스코어 업데이트 핸들러 (스코어 플래시 포함) ─── */
  const handleLiveUpdate = useCallback((data) => {
    if (!shouldAcceptSocketPayload(bettingType, data, isKnownLiveSocketPayload)) return;
    const {
      match_id,
      live_score_home,
      live_score_away,
      live_minute,
      live_period,
      live_seconds,
      live_clock_updated_at,
      fixture_status,
      fixture_status_label,
      is_visible,
      visible,
      user_visible,
      isUserVisible,
    } = data;
    const receivedAt = Date.now();
    if (match_id == null) return;
    if (!shouldApplyRealtimeOrderingGuard(displayOrderingGuardRef.current, 'match', match_id, data)) return;
    commitRealtimeOrderingGuard(displayOrderingGuardRef.current, 'match', match_id, data);
    markWsEventReceived({
      lastLiveUpdateReceivedAt: receivedAt,
      lastScoreStatusUpdateReceivedAt: receivedAt,
      browserReceiveAt: receivedAt,
      lastLiveUpdateMatchId: match_id,
    });

    const updateMatchList = (list) =>
      list.map(m => {
        if (!isSameSocketId(m.id, match_id)) return m;
        /** 스코어 변경 감지 → 플래시 애니메이션 */
        const scoreChanged = (live_score_home !== undefined && m.live_score_home !== live_score_home) ||
                             (live_score_away !== undefined && m.live_score_away !== live_score_away);
        return {
          ...m,
          ...(live_score_home !== undefined && { live_score_home }),
          ...(live_score_away !== undefined && { live_score_away }),
          ...(live_minute !== undefined && { live_minute }),
          ...(live_period !== undefined && { live_period }),
          ...(live_seconds !== undefined && { live_seconds }),
          ...(live_clock_updated_at !== undefined && { live_clock_updated_at }),
          ...(fixture_status !== undefined && { fixture_status }),
          ...(fixture_status_label !== undefined && { fixture_status_label }),
          ...(is_visible !== undefined && { is_visible }),
          ...(visible !== undefined && { visible }),
          ...(user_visible !== undefined && { user_visible }),
          ...(isUserVisible !== undefined && { isUserVisible }),
          _liveShellUpdatedAt: receivedAt,
          _liveShellSource: 'ws_live_update',
          _scoreFlash: scoreChanged || false
        };
      });

    setLiveMatchStateByMatchId(prev => ({
      ...prev,
      [String(match_id)]: {
        ...(prev[String(match_id)] || {}),
        ...(live_score_home !== undefined && { live_score_home }),
        ...(live_score_away !== undefined && { live_score_away }),
        ...(live_minute !== undefined && { live_minute }),
        ...(live_period !== undefined && { live_period }),
        ...(live_seconds !== undefined && { live_seconds }),
        ...(live_clock_updated_at !== undefined && { live_clock_updated_at }),
        ...(fixture_status !== undefined && { fixture_status }),
        ...(fixture_status_label !== undefined && { fixture_status_label }),
        ...(is_visible !== undefined && { is_visible }),
        ...(visible !== undefined && { visible }),
        ...(user_visible !== undefined && { user_visible }),
        ...(isUserVisible !== undefined && { isUserVisible }),
        ...(data.authority_ts ? { authority_ts: data.authority_ts } : {}),
        ...(data.sequence !== undefined ? { sequence: data.sequence } : {}),
        updatedAt: Date.now(),
        source: 'ws_inplay_fixture_state',
        _liveShellUpdatedAt: receivedAt,
        _liveShellSource: 'ws_live_update',
      },
    }));
    setMatches(prev => updateMatchList(prev));
    if (isSocketMatchHiddenPayload(data)) {
      clearInplayMatchMarkets(match_id, {
        source: 'ws_live_update_hidden',
        hidden: true,
        authority_ts: data?.authority_ts,
        sequence: data?.sequence,
      });
    }
    const markRendered = () => markInplayV2BrowserDebug({
      lastLiveUpdateRenderedAt: Date.now(),
      lastScoreStatusUpdateRenderedAt: Date.now(),
      lastRenderedAt: Date.now(),
      lastRenderAt: Date.now(),
      browserRenderAt: Date.now(),
      lastLiveUpdateRenderDelayMs: Date.now() - receivedAt,
      lastLiveUpdateMatchId: match_id,
    });
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(markRendered);
    } else {
      setTimeout(markRendered, 0);
    }

    /** 1.5초 후 플래시 해제 */
    setTimeout(() => {
      setMatches(prev => prev.map(m => isSameSocketId(m.id, match_id) ? { ...m, _scoreFlash: false } : m));
    }, 1500);
  }, [bettingType, clearInplayMatchMarkets, isKnownLiveSocketPayload, markWsEventReceived]);

  const handleSportsnewFixturePatch = useCallback((raw) => {
    const { sequence, emittedAt, patch } = normalizeSportsnewFixturePatchEnvelope(raw);
    const matchId = Number(patch?.fixtureId ?? patch?.fixture_id ?? patch?.fixture?.id ?? patch?.id);
    if (!Number.isFinite(matchId)) return;
    const basePayload = {
      match_id: matchId,
      matchId,
      id: matchId,
      betting_category: 'LIVE',
      authority_ts: patch?.fixture?.updatedAt ?? patch?.fixture?.updated_at ?? emittedAt ?? patch?.emittedAt ?? null,
      sequence,
    };

    if (Array.isArray(patch?.marketSnapshot) || Array.isArray(patch?.markets)) {
      const markets = Array.isArray(patch?.marketSnapshot) ? patch.marketSnapshot : patch.markets;
      handleMarketsReplace({
        ...basePayload,
        markets,
        projectionScope: patch?.projectionScope ?? patch?.projection_scope ?? (Array.isArray(patch?.marketSnapshot) ? 'full' : undefined),
        isFullFixtureProjection: patch?.isFullFixtureProjection ?? patch?.is_full_fixture_projection ?? Array.isArray(patch?.marketSnapshot),
        replaceReason: patch?.replaceReason ?? patch?.replace_reason ?? 'sportsnew_fixture_patch',
        eventTypes: patch?.eventTypes ?? patch?.event_types,
      });
    }

    if (Array.isArray(patch?.odds) && patch.odds.length > 0) {
      handleMarketsPatch({
        ...basePayload,
        patches: patch.odds.map((odd) => sportsnewOddPatchForMarketsPatch(odd, patch)),
        eventTypes: patch?.eventTypes ?? patch?.event_types,
      });
    }

    const fixture = patch?.fixture;
    if (fixture) {
      const score = fixture.score || {};
      handleLiveUpdate({
        ...basePayload,
        live_score_home: fixture.scoreHome ?? fixture.score_home ?? score.home,
        live_score_away: fixture.scoreAway ?? fixture.score_away ?? score.away,
        live_period: fixture.periodLabel ?? fixture.period_label ?? fixture.period,
        live_seconds: fixture.clockSeconds ?? fixture.clock_seconds,
        live_clock_updated_at: fixture.updatedAt ?? fixture.updated_at,
        fixture_status: fixture.status,
        fixture_status_label: fixture.statusLabel ?? fixture.status_label,
      });
    }
  }, [handleLiveUpdate, handleMarketsPatch, handleMarketsReplace]);

  /* ─── 마켓 중지/재개 핸들러 ─── */
  const handleMarketSuspend = useCallback((data) => {
    if (!shouldAcceptSocketPayload(bettingType, data, isKnownLiveSocketPayload)) return;
    const { match_id, market_id, is_suspended, odds_ids } = data;
    const affectedOddIds = Array.isArray(odds_ids) ? odds_ids : [];
    const receivedAt = Date.now();
    if (market_id == null) return;
    const marketDisplayGuardKey = getCustomerMarketKey(data);
    if (!shouldApplyRealtimeOrderingGuard(displayOrderingGuardRef.current, 'market', marketDisplayGuardKey, data)) return;
    commitRealtimeOrderingGuard(displayOrderingGuardRef.current, 'market', marketDisplayGuardKey, data);
    markWsEventReceived();

    /** 상세 패널 stale 보정 — market + odds suspend */
    setMarketLatest(prev => ({
      ...prev,
      [marketDisplayGuardKey]: {
        ...(prev[marketDisplayGuardKey] || {}),
        is_suspended,
        updatedAt: receivedAt,
        source: 'ws',
        ...(data.authority_ts ? { authority_ts: data.authority_ts } : {}),
        ...(data.sequence !== undefined ? { sequence: data.sequence } : {}),
        ...(data.market_state_version !== undefined ? { sequence: data.market_state_version } : {}),
      },
    }));
	    if (affectedOddIds.length) {
	      const acceptedOddIds = affectedOddIds.filter((oid) => (
	        commitMarketSuspendOddDisplayGuard(displayOrderingGuardRef.current, oid, data)
	      ));
	      setOddsLatest(prev => {
	        const next = { ...prev };
	        acceptedOddIds.forEach(oid => {
	          const displayGuardPayload = buildMarketSuspendOddDisplayGuardPayload(oid, data);
	          next[oid] = {
	            ...(next[oid] || {}),
	            is_suspended,
	            ...(displayGuardPayload.authority_ts ? { authority_ts: displayGuardPayload.authority_ts } : {}),
	            ...(displayGuardPayload.sequence !== undefined ? { sequence: displayGuardPayload.sequence } : {}),
	            updatedAt: receivedAt,
	            source: 'ws_market_suspend',
	          };
	        });
	        return next;
	      });
	      if (cartCtxRef.current) {
	        acceptedOddIds.forEach(oid => {
	          cartCtxRef.current(oid, undefined, { is_suspended });
	        });
	      }
    }
  }, [bettingType, isKnownLiveSocketPayload, markWsEventReceived]);

  /* ─── 경기 상태 변경 핸들러 ─── */
  const handleMatchStatus = useCallback((data) => {
    if (!shouldAcceptSocketPayload(bettingType, data, isKnownLiveSocketPayload)) return;
    const match_id = data?.match_id ?? data?.matchId ?? data?.id;
    const { status } = data;
    if (match_id == null) return;
    if (!shouldApplyRealtimeOrderingGuard(displayOrderingGuardRef.current, 'match', match_id, data)) return;
    commitRealtimeOrderingGuard(displayOrderingGuardRef.current, 'match', match_id, data);
    const receivedAt = Date.now();
    markWsEventReceived({
      lastMatchStatusReceivedAt: receivedAt,
      lastMatchStatusMatchId: match_id,
      lastMatchStatus: status,
      browserReceiveAt: receivedAt,
    });
    const isTerminalStatus = isTerminalLiveMatchStatus(data) || isTerminalLiveScoreShell(data);
    const isHiddenStatus = isTerminalStatus || isSocketMatchHiddenPayload(data) || isHiddenLiveScoreShell(data);
    const shellSnapshot = buildLiveShellSnapshotFromSocket({
      ...data,
      ...(isHiddenStatus ? { is_live: false, is_visible: false, visible: false, user_visible: false, isUserVisible: false } : {}),
      ...(status === 'LIVE' && !isHiddenStatus ? { is_live: true } : {}),
    }, { matchId: match_id });
    syncLiveMatchShellFromSnapshot(shellSnapshot, {
      matchId: match_id,
      source: 'ws_match_status_shell',
      authority_ts: data?.authority_ts,
      sequence: data?.sequence,
    });
    if (isHiddenStatus) {
      setTerminalLiveMatchIds((prev) => (
        prev.some((id) => isSameSocketId(id, match_id)) ? prev : [...prev, match_id]
      ));
    }

    const hasExplicitLiveFlag = data?.is_live !== undefined || data?.isLive !== undefined;
    const explicitLiveFlag = data?.is_live ?? data?.isLive;
    const updateMatchList = (list) =>
      list.map(m => {
        if (!isSameSocketId(m.id, match_id)) return m;
        return {
          ...m,
          ...(status !== undefined ? { status } : {}),
          ...(status === 'LIVE' ? { is_live: true } : {}),
          ...(hasExplicitLiveFlag ? { is_live: Boolean(explicitLiveFlag) } : {}),
          ...(isHiddenStatus && {
            is_live: false,
            is_visible: false,
            visible: false,
            user_visible: false,
            isUserVisible: false,
          }),
        };
      });

    setMatches(prev => updateMatchList(prev));
    if (isSocketMatchHiddenPayload(data)) {
      clearInplayMatchMarkets(match_id, {
        source: 'ws_match_status_hidden',
        hidden: true,
        status,
        authority_ts: data?.authority_ts,
        sequence: data?.sequence,
      });
    }

    if (isHiddenStatus) {
      clearInplayMatchMarkets(match_id, {
        source: 'ws_match_status_terminal',
        hidden: true,
        is_live: false,
        status,
        authority_ts: data?.authority_ts,
        sequence: data?.sequence,
      });
      setMatches(prev => prev.filter(m => !isSameSocketId(m.id, match_id)));
      markInplayV2BrowserDebug({
        lastMatchStatusRenderedAt: Date.now(),
        lastMatchStatusImmediateRemovedAt: Date.now(),
        lastMatchStatusRemovedMatchId: match_id,
        lastRenderedAt: Date.now(),
        browserRenderAt: Date.now(),
      });
    }
  }, [bettingType, clearInplayMatchMarkets, isKnownLiveSocketPayload, markWsEventReceived, syncLiveMatchShellFromSnapshot]);

  const emitSubscriptionsRef = useRef(emitSubscriptions);
  const handleSubscribeAckRef = useRef(handleSubscribeAck);
  const handleOddsUpdateRef = useRef(handleOddsUpdate);
  const handleOddsBulkUpdateRef = useRef(handleOddsBulkUpdate);
  const handleMarketsReplaceRef = useRef(handleMarketsReplace);
  const handleMarketsPatchRef = useRef(handleMarketsPatch);
  const handleSportsnewFixturePatchRef = useRef(handleSportsnewFixturePatch);
  const handleLiveUpdateRef = useRef(handleLiveUpdate);
  const handleLiveListInvalidateRef = useRef(handleLiveListInvalidate);
  const handleMarketSuspendRef = useRef(handleMarketSuspend);
  const handleMatchStatusRef = useRef(handleMatchStatus);

  useEffect(() => {
    emitSubscriptionsRef.current = emitSubscriptions;
    handleSubscribeAckRef.current = handleSubscribeAck;
    handleOddsUpdateRef.current = handleOddsUpdate;
    handleOddsBulkUpdateRef.current = handleOddsBulkUpdate;
    handleMarketsReplaceRef.current = handleMarketsReplace;
    handleMarketsPatchRef.current = handleMarketsPatch;
    handleSportsnewFixturePatchRef.current = handleSportsnewFixturePatch;
    handleLiveUpdateRef.current = handleLiveUpdate;
    handleLiveListInvalidateRef.current = handleLiveListInvalidate;
    handleMarketSuspendRef.current = handleMarketSuspend;
    handleMatchStatusRef.current = handleMatchStatus;
  }, [
    emitSubscriptions,
    handleSubscribeAck,
    handleOddsUpdate,
    handleOddsBulkUpdate,
    handleMarketsReplace,
    handleMarketsPatch,
    handleSportsnewFixturePatch,
    handleLiveUpdate,
    handleLiveListInvalidate,
    handleMarketSuspend,
    handleMatchStatus,
  ]);

  /* ─── Socket.IO 연결 ─── */
  useEffect(() => {
    const backUrl = process.env.REACT_APP_BACK_URL;
    if (!backUrl) return;
    if (!shouldConnectSportsSocket) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      subscriptionKeyRef.current = '';
      subscribedRoomsRef.current = new Set();
      subscribedSubscriptionByRoomRef.current = new Map();
      subscribedSportsnewFixtureRoomsRef.current = new Map();
      clearSubscribeAckTimer();
      setSocketConnected(false);
      return;
    }

    const socketOptions = {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    };

    if (sportsSocketAuthToken) {
      socketOptions.auth = { token: sportsSocketAuthToken };
    }

    const socket = io(`${backUrl}/ws/sports`, socketOptions);

    socket.on('connect', () => {
      console.log('⚽ Sports WebSocket 연결됨');
      const now = Date.now();
      setSocketConnected(true);
      setLastSocketConnectedAt(now);
      setSocketRecoveryNonce((value) => value + 1);
      subscribedRoomsRef.current = new Set();
      subscribedSubscriptionByRoomRef.current = new Map();
      subscribedSportsnewFixtureRoomsRef.current = new Map();
      subscriptionKeyRef.current = '';
      /** 연결/재연결 시 현재 match 목록 구독 */
      emitSubscriptionsRef.current(true);
    });

    socket.on('disconnect', () => {
      clearSubscribeAckTimer();
      setSocketConnected(false);
      subscribedSportsnewFixtureRoomsRef.current = new Map();
    });

    socket.on('subscribe:match:ack', (...args) => handleSubscribeAckRef.current(...args));

    socket.on('subscribe:matches:ack', (...args) => handleSubscribeAckRef.current(...args));

    socket.on('connect_error', (err) => {
      console.warn('⚽ Sports WebSocket 연결 실패:', err.message);
    });

    /** 이벤트 리스너 등록 */
    socket.on('odds:update', (...args) => handleOddsUpdateRef.current(...args));
    socket.on('odds:bulk_update', (...args) => handleOddsBulkUpdateRef.current(...args));
    socket.on('markets:replace', (...args) => handleMarketsReplaceRef.current(...args));
    socket.on('markets:patch', (...args) => handleMarketsPatchRef.current(...args));
    socket.on('sportsnew:fixture:patch', (...args) => handleSportsnewFixturePatchRef.current(...args));
    socket.on('live:update', (...args) => handleLiveUpdateRef.current(...args));
    socket.on('live:list:invalidate', (...args) => handleLiveListInvalidateRef.current(...args));
    socket.on('market:suspend', (...args) => handleMarketSuspendRef.current(...args));
    socket.on('match:status', (...args) => handleMatchStatusRef.current(...args));

    /** 베팅 결과 이벤트 */
    socket.on('bet:result', (data) => {
      console.log('⚽ 베팅 결과:', data);
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
      subscriptionKeyRef.current = '';
      subscribedRoomsRef.current = new Set();
      subscribedSubscriptionByRoomRef.current = new Map();
      subscribedSportsnewFixtureRoomsRef.current = new Map();
      clearSubscribeAckTimer();
      setSocketConnected(false);
    };
  }, [shouldConnectSportsSocket, sportsSocketAuthToken, clearSubscribeAckTimer]);

  /* ─── 경기 구독: matches/detail 변경 시 match room 재구독 ─── */
  useEffect(() => {
    emitSubscriptions();
  }, [matches, detailMatchIds, emitSubscriptions]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket?.connected) return undefined;
    if (bettingType !== 'LIVE' || location.pathname !== '/sports') return undefined;

    const subscription = {
      betting_category: 'LIVE',
      sport_id: selectedSportId || null,
    };
    socket.emit('subscribe:live-list', subscription);
    return () => {
      socket.emit('unsubscribe:live-list', subscription);
    };
  }, [bettingType, location.pathname, selectedSportId, socketConnected]);

  useEffect(() => () => {
    if (liveListRefreshTimerRef.current) {
      clearTimeout(liveListRefreshTimerRef.current);
      liveListRefreshTimerRef.current = null;
    }
  }, []);

  /* ─── 안전 폴링: 국내/해외/스페셜은 현재 카테고리만 visible tab에서 60초마다 REST 재동기화 ─── */
  useEffect(() => {
    const isSportsOddsRoute = location.pathname === '/sports' || location.pathname.startsWith('/sports/match/');
    if (!canFetchMatches || !isSportsOddsRoute) return undefined;

    const interval = setInterval(() => {
      if (isDocumentHidden()) return;

      const shouldPollCurrentCategory = ALWAYS_REST_POLL_BETTING_TYPES.has(bettingType);
      const shouldRunSocketFallback = bettingType !== 'LIVE' && !socketRef.current?.connected;
      if (!shouldPollCurrentCategory && !shouldRunSocketFallback) return;

      // list refresh — 내부적으로 eachDisplayedOdd로 markets[] + handicap_pair 모두 sync
      fetchMatches(matchPage || 1, false);
      // 열린 detail match 재조회 (odds 동기화)
      const _detIds = [...detailMatchIds];
      for (const mid of _detIds) {
        const requestStartedAt = Date.now();
        SportsService.getMatchOdds(mid, {
          betting_category: bettingType,
          ...(bettingType === 'LIVE' ? { compact: 1 } : {}),
          ...(bettingType === 'LIVE' ? { _live_ts: Date.now() } : {}),
        }).then(res => {
          if (!res?.status || !res?.data) return;
          const _applySel = (sel) => {
            const selId = Number(sel.id);
            if (!selId) return;
	            const prevEntry = oddsLatestRef.current?.[selId];
	            const now = Date.now();
	            if (!shouldApplyRealtimeOrderingGuard(displayOrderingGuardRef.current, 'odd', selId, sel)) return;
	            if (shouldPreserveRecentRealtimeOdds(prevEntry, sel, { now, requestStartedAt })) return;
	            commitRealtimeOrderingGuard(displayOrderingGuardRef.current, 'odd', selId, sel);
	            const prevOdds = prevEntry?.odds;
            const newOdds = Number(sel.odds);
            if (bettingType !== 'LIVE' && prevOdds !== undefined && prevOdds !== newOdds) {
              const diff = Math.round((newOdds - prevOdds) * 100) / 100;
              if (diff > 0.001) flashOdds(selId, 'up', diff);
              else if (diff < -0.001) flashOdds(selId, 'down', Math.abs(diff));
            }
            setOddsLatest(prev => ({
              ...prev,
              [selId]: buildOddsLatestEntry(prev[selId], sel, {
                now,
                requestStartedAt,
                source: 'rest_detail_poll',
              }),
            }));
            if (cartCtxRef.current) cartCtxRef.current(selId, newOdds);
          };
          // markets[].selections|odds — 표준 경로
          const markets = res.data.markets || [];
          for (const mk of markets) {
            for (const sel of getMarketOdds(mk)) _applySel(sel);
          }
          // detail 응답에 handicap_pair가 추가되면(API 후속 작업) 자동으로 동작하도록 방어적 walk
          const hp = res.data.handicap_pair;
          if (hp) {
            if (hp.home_selection) _applySel(hp.home_selection);
            if (hp.away_selection) _applySel(hp.away_selection);
          }
          // 현재 list state의 같은 match handicap_pair backup walk —
          //   detail이 handicap_pair를 emit 안하더라도 list-driven board에서 카트의 HC selection이
          //   이미 보유 중이면 list state ref 기준으로 cart sync를 한 번 더 보장
          const stateMatch = (matchesRef.current || []).find(m => Number(m.id) === Number(mid));
          const stateHp = stateMatch?.handicap_pair;
          if (stateHp) {
            if (stateHp.home_selection) _applySel(stateHp.home_selection);
            if (stateHp.away_selection) _applySel(stateHp.away_selection);
          }
        }).catch(() => {});
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [canFetchMatches, location.pathname, fetchMatches, matchPage, detailMatchIds, SportsService, bettingType, flashOdds]);

  /* ─── 30초마다 라이브 카운트 갱신 ─── */
  useEffect(() => {
    if (pauseOldApiFetches) return undefined; // SportsnewContext polls live-count
    const interval = setInterval(fetchLiveCount, 30000);
    return () => clearInterval(interval);
  }, [pauseOldApiFetches, fetchLiveCount]);

  /** 배당 변경 에러 후 강제 재동기화: 현재 페이지 유지 + 카트 selection 중심 odds 갱신 */
  const forceResyncOdds = useCallback(async (targetSelections = []) => {
    // 1) 현재 페이지 기준 list API 재조회 — DOMESTIC handicap_pair 포함 fresh rows 확보
    //    fetchMatches는 eachDisplayedOdd로 markets[] + handicap_pair 모두 cart/oddsLatest sync 함
    const rows = await fetchMatches(matchPage || 1, false, {
      forceCurrentOddsPath: true,
    });

    if (!cartCtxRef.current || targetSelections.length === 0) return;
    const matchIds = [...new Set(targetSelections.map(s => Number(s.match_id)).filter(Boolean))];
    const oddsIdSet = new Set(targetSelections.map(s => Number(s.odds_id)).filter(Boolean));

    // 2) list rows에서 cart selection id를 직접 매칭하여 cart + oddsLatest 명시적 갱신
    //    fetchMatches 내부 sync는 React commit 타이밍 의존이라 여기서 ref 기준으로 한 번 더 보장
    const matchedFromList = new Set();
    if (Array.isArray(rows) && rows.length > 0) {
      const oddsLatestPatch = {};
      const now = Date.now();
      for (const m of rows) {
        if (!matchIds.includes(Number(m.id))) continue;
        eachDisplayedOdd(m, o => {
          const selId = Number(o.id);
          if (oddsIdSet.has(selId)) {
            const prevEntry = oddsLatestRef.current?.[selId];
            commitRealtimeOrderingGuard(displayOrderingGuardRef.current, 'odd', selId, o);
            cartCtxRef.current(selId, o.odds);
            oddsLatestPatch[selId] = buildOddsLatestEntry(prevEntry, o, {
              now,
              source: 'rest_force_resync',
              forceOverwrite: true,
            });
            matchedFromList.add(selId);
          }
        });
      }
      if (Object.keys(oddsLatestPatch).length > 0) {
        setOddsLatest(prev => ({ ...prev, ...oddsLatestPatch }));
      }
    }

    // 3) list 응답에서 못 찾은 id만 detail API로 추가 보강 (예: detail-only market)
    const missingIds = new Set([...oddsIdSet].filter(id => !matchedFromList.has(id)));
    if (missingIds.size === 0) return;
    for (const mid of matchIds) {
      try {
        const requestStartedAt = Date.now();
        const res = await SportsService.getMatchOdds(mid, {
          betting_category: bettingType,
          ...(bettingType === 'LIVE' ? { compact: 1 } : {}),
          ...(bettingType === 'LIVE' ? { _live_ts: Date.now() } : {}),
        });
        if (!res?.status || !res?.data) continue;
        const markets = res.data.markets || [];
        const oddsLatestPatch = {};
        const now = Date.now();
        for (const mk of markets) {
          for (const sel of getMarketOdds(mk)) {
            const selId = Number(sel.id);
            if (missingIds.has(selId)) {
              const prevEntry = oddsLatestRef.current?.[selId];
              commitRealtimeOrderingGuard(displayOrderingGuardRef.current, 'odd', selId, sel);
              cartCtxRef.current(selId, sel.odds);
              oddsLatestPatch[selId] = buildOddsLatestEntry(prevEntry, sel, {
                now,
                requestStartedAt,
                source: 'rest_force_resync',
                forceOverwrite: true,
              });
            }
          }
        }
        // detail 응답에 handicap_pair shape이 추가되면(API 후속 작업) 자동으로 동작하도록 방어적 walk
        const hp = res.data.handicap_pair;
        if (hp) {
          for (const sel of [hp.home_selection, hp.away_selection].filter(Boolean)) {
            const selId = Number(sel.id);
            if (missingIds.has(selId)) {
              const prevEntry = oddsLatestRef.current?.[selId];
              commitRealtimeOrderingGuard(displayOrderingGuardRef.current, 'odd', selId, sel);
              cartCtxRef.current(selId, sel.odds);
              oddsLatestPatch[selId] = buildOddsLatestEntry(prevEntry, sel, {
                now,
                requestStartedAt,
                source: 'rest_force_resync',
                forceOverwrite: true,
              });
            }
          }
        }
        if (Object.keys(oddsLatestPatch).length > 0) {
          setOddsLatest(prev => ({ ...prev, ...oddsLatestPatch }));
        }
      } catch (_) {}
    }
  }, [fetchMatches, matchPage, SportsService, bettingType]);

  const mergeInplaySnapshot = useCallback((currentMatch, incomingMatch, options = {}) => (
    mergeInplayMatchWithDisplayGuard(
      currentMatch,
      incomingMatch,
      displayOrderingGuardRef.current,
      {
        source: options.source || 'rest_snapshot',
        ...(options.replaceMarkets === true ? { replaceMarkets: true } : {}),
        ...(options.replaceSelections === true ? { replaceSelections: true } : {}),
      }
    )
  ), []);

  const syncMatchDetailIntoList = useCallback((matchDetail, options = {}) => {
    const matchId = Number(matchDetail?.id ?? options.matchId);
    if (!matchId) return;

    const detailMarkets = Array.isArray(options.markets)
      ? options.markets
      : (Array.isArray(matchDetail?.markets) ? matchDetail.markets : null);
    const requestStartedAt = Number(options.requestStartedAt || 0);
    const source = options.source || 'rest_detail';

    syncLiveMatchShellFromSnapshot(matchDetail, {
      matchId,
      requestStartedAt,
      source: `${source}_shell`,
      authority_ts: options.authority_ts,
      sequence: options.sequence,
    });

    if (detailMarkets?.length) {
      setMarketLatest(prev => {
        const next = { ...prev };
        detailMarkets.forEach(market => {
          const marketId = getCustomerMarketKey(market);
          if (marketId == null) return;
          if (!shouldApplyRealtimeOrderingGuard(displayOrderingGuardRef.current, 'market', marketId, market)) return;
          commitRealtimeOrderingGuard(displayOrderingGuardRef.current, 'market', marketId, market);
          const nextSuspended = (
            isLockedFlag(market.is_suspended)
            || isLockedFlag(market.suspended)
            || isLockedFlag(market.betting_locked)
            || isLockedFlag(market.non_tradable)
          );
	          const prevEntry = next[marketId];
	          next[marketId] = {
            ...(prevEntry || {}),
            is_suspended: nextSuspended,
            updatedAt: Date.now(),
            source,
            ...(market.authority_ts ? { authority_ts: market.authority_ts } : {}),
            ...(market.sequence !== undefined ? { sequence: market.sequence } : {}),
            ...(market.market_state_version !== undefined ? { sequence: market.market_state_version } : {}),
          };
        });
        return next;
      });
    }

    if (bettingType === 'LIVE' && Array.isArray(detailMarkets) && detailMarkets.length === 0 && options.clearStale === true) {
      clearInplayMatchMarkets(matchId, {
        source,
        hidden: options.hidden === true,
        is_live: options.is_live,
        status: options.status,
        authority_ts: options.authority_ts,
        sequence: options.sequence,
      });
      return;
    }

    if (bettingType === 'LIVE' && detailMarkets?.length) {
      const incomingDetailProjection = {
        ...matchDetail,
        id: matchId,
        match_id: matchId,
        markets: detailMarkets,
        ...(options.authority_ts ? { authority_ts: options.authority_ts } : {}),
        ...(options.sequence !== undefined ? { sequence: options.sequence } : {}),
      };
      setLiveMarketProjectionByMatchId(prev => {
        const key = String(matchId);
        const previousEntry = prev?.[key] || null;
        const previousMatch = (matchesRef.current || []).find(match => isSameSocketId(match?.id, matchId));
        const baseMarkets = Array.isArray(previousEntry?.markets)
          ? previousEntry.markets
          : previousMatch?.markets || [];
        const preservesAuthoritativeEmpty = (
          previousEntry?.isFullFixtureProjection === true
          && baseMarkets.length === 0
          && compareRealtimeOrderingInfo(
            normalizeRealtimeOrderingInfo(incomingDetailProjection),
            normalizeRealtimeOrderingInfo(previousEntry)
          ) <= 0
        );
        if (preservesAuthoritativeEmpty) return prev;

        const mergedMarkets = mergeInplayMatchWithDisplayGuard(
          { id: matchId, match_id: matchId, markets: baseMarkets },
          incomingDetailProjection,
          displayOrderingGuardRef.current,
          { source }
        )?.markets || detailMarkets;
        return {
          ...prev,
          [key]: {
            ...(previousEntry || {}),
            markets: mergedMarkets,
            updatedAt: Date.now(),
            source,
          },
        };
      });
      return;
    }

    if (bettingType === 'LIVE') return;

    setMatches(prev => prev.map(match => {
      if (Number(match?.id) !== matchId) return match;
      return mergeInplayMatchWithDisplayGuard(
        match,
        {
          ...matchDetail,
          id: matchId,
          ...(detailMarkets?.length ? { markets: detailMarkets } : {}),
        },
        displayOrderingGuardRef.current,
        { source }
      );
    }));
  }, [bettingType, clearInplayMatchMarkets, syncLiveMatchShellFromSnapshot]);

  /**
   * detail/외부 로드 시 odds baseline 등록 — 이후 polling/ws에서 비교 가능하게.
   *
   * @param {Array} markets - 응답의 markets 배열
   * @param {object} [opts]
   * @param {boolean} [opts.overwrite=false]
   *   - false (기본): 기존 oddsLatest 값이 있으면 보존 (신규 entry만 추가)
   *   - true: 기존 entry도 fresh 값으로 덮어쓰기 (detail API 재로드 시 stale 해소용)
   */
  const primeOddsLatest = useCallback((markets, opts = {}) => {
    if (!markets?.length) return;
    const overwrite = opts.overwrite === true;
    const flashChanges = opts.flashChanges === true;
    const requestStartedAt = Number(opts.requestStartedAt || 0);
    const source = opts.source || 'rest';
    setOddsLatest(prev => {
      const next = { ...prev };
      const now = Date.now();
      for (const mk of markets) {
        for (const o of (mk.odds || mk.selections || [])) {
          const id = Number(o.id);
          const prevEntry = next[id];
          if (!shouldApplyRealtimeOrderingGuard(displayOrderingGuardRef.current, 'odd', id, o)) {
            continue;
          }
          if (overwrite && shouldPreserveRecentRealtimeOdds(prevEntry, o, { now, requestStartedAt })) {
            continue;
          }
          const orderingInfo = commitRealtimeOrderingGuard(displayOrderingGuardRef.current, 'odd', id, o);
          const nextOdds = Number(o.odds);
          const prevOdds = prevEntry?.odds;
          if (
            flashChanges &&
            prevOdds !== undefined &&
            Number.isFinite(prevOdds) &&
            Number.isFinite(nextOdds) &&
            Math.abs(nextOdds - prevOdds) > 0.001
          ) {
            const diff = Math.round(Math.abs(nextOdds - prevOdds) * 100) / 100;
            flashOdds(id, nextOdds > prevOdds ? 'up' : 'down', diff);
          }
          if (overwrite || !next[id] || orderingInfo) {
            next[id] = buildOddsLatestEntry(prevEntry, o, {
              now,
              requestStartedAt,
              source,
            });
          }
        }
      }
      return next;
    });
  }, [flashOdds]);

  const value = {
    categories, selectedSport: effectiveSelectedSport, setSelectedSport,
    leagues, selectedLeagueId, setSelectedLeagueId,
    matches, loading, pageLoading, oddsFlash, oddsLatest, marketLatest, liveMarketProjectionByMatchId, liveMatchStateByMatchId,
    bettingType, setBettingType, searchQuery, setSearchQuery, sortOrder, setSortOrder,
    popularMode, setPopularMode,
    liveCount, matchCounts, displaySettings,
    socketConnected, getLastWsEventAt, lastSocketConnectedAt, socketRecoveryNonce, lastSubscribeAckAt, sportsSocketDebug,
    liveCartMatchIds, terminalLiveMatchIds,
    setPauseOldApiFetches,
    refreshMatches: fetchMatches, refreshMatchCounts: fetchMatchCounts,
    forceResyncOdds, primeOddsLatest, syncMatchDetailIntoList, clearInplayMatchMarkets,
    mergeInplaySnapshot,
    goToPage, currentPage, livePendingPage, totalPages, hasMoreMatches, hasPrevPage, matchTotal: scopedMatchTotal,
    matchTotalScopeKey, currentMatchTotalScopeKey: listQueryResetKey,
    registerDetailMatch, unregisterDetailMatch,
  };

  return <SportsContext.Provider value={value}>{children}</SportsContext.Provider>;
};
